import { authoredLines } from '@lite-workspace/text-layout';
import { FC_PersonTypeEnum, Person, PersonTypeEnum, Teacher } from '@lite-workspace/dto-fotoclass-photo';
import {
  computeVignetteImagePlacement,
  forEachDrawableDeep,
  isDrawableGroup,
  isDrawableText,
  isPackSampleSlot,
  isRosterSurface,
  rosterCellPredicate,
  rosterCellPredicateBox,
  type DrawableData,
  type DrawableGroupData,
  type DrawableTextData,
  type RosterCellLike,
  type RosterSurface,
  type SpreadBoxesLike,
  type SpreadData,
  type VignettePlacement,
} from '@lite-workspace/photobook-model';
import {
  FioLayout,
  GroupPeopleType,
  PERSON_TEXT_TYPES,
  POSITIONAL_PERSON_TEXT_TYPES,
  TextTypeEnum,
  VignetteTextTypeEnum,
  isForeignToPersonText,
  isPersonMarkupField,
  isPersonTextField,
  personOwnsGroupText,
  type CustomGroupTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';
import { textPlaceholderFor } from '../text-placeholders';
import { captionEms } from './caption-ems.util';

// Закон «кому принадлежит текст в персонной группе» переехал в dto-fotoclass-templates: его читает
// и ОСВОБОЖДЕНИЕ (photobook-model), а тот от этого пакета не зависит. Ре-экспорт — чтобы прежние
// потребители не переписывались; определение одно.
export { PERSON_TEXT_TYPES, POSITIONAL_PERSON_TEXT_TYPES, isForeignToPersonText, isPersonTextField, personOwnsGroupText };
import { NameFormat, type TeacherNameFormat, type VignetteCaptionSizes, type VignetteKnobs } from '../project.dto';
import { SpreadTypeEnum } from '../spread.dto';
import type { VignetteQuoteGroupStates } from '../vignette-quote-groups';
import { blankOffGroupQuotes } from './vignette-quote-visibility.util';
import { fitVignetteQuote, fitVignetteQuotes, vignetteQuoteLimit } from './vignette-quote-fit.util';
import type { QuotesSettings } from '../quotes.dto';

/** Подписи виньетки: формат учеников, формат педагогов (`null` = раскладка пака/движка), строка предмета, кегли класса. */
export interface VignetteCaptionContext {
  nameFormat: NameFormat;
  teacherNameFormat: TeacherNameFormat | null;
  teacherSubject: boolean;
  /** «Как в паке» для всех: на виньетке формат не действует, ФИО печатает раскладка рамки (одна безымянная строка — целиком). */
  fromPack?: boolean;
  captionSizes?: VignetteCaptionSizes | null;
}

export function vignetteCaptionContext(
  vignette:
    | {
        nameFormat?: NameFormat;
        reverseNameOrder?: boolean;
        teacherNameFormat?: VignetteKnobs['teacherNameFormat'];
        teacherSubject?: boolean;
        captionsFromPack?: boolean;
        design?: VignetteKnobs['design'];
        /** Уже собранный контекст: гейт пустых текстов и заморозка утверждения оборачивают его ещё раз. */
        fromPack?: boolean;
        captionSizes?: VignetteKnobs['captionSizes'];
      }
    | null
    | undefined,
): VignetteCaptionContext {
  return {
    nameFormat: deriveNameFormat(vignette?.nameFormat, vignette?.reverseNameOrder),
    teacherNameFormat: vignette?.teacherNameFormat ?? null,
    teacherSubject: vignette?.teacherSubject ?? true,
    // У виньетки Люмир своей раскладки пака нет: ручка, оставшаяся от пака, на ней не действует.
    fromPack: (vignette?.fromPack === true || vignette?.captionsFromPack === true) && vignette?.design !== 'lumir',
    captionSizes: vignette?.captionSizes ?? null,
  };
}

/**
 * Голый формат — прежний закон: один формат на обе роли, предмет виден, `fioLayout` бокса главнее.
 * `formatWins` — поверхность виньетки: формат роли переписывает порядок, инициалы и перенос бокса.
 */
export type CaptionRules = {
  nameFormat: NameFormat;
  teacherNameFormat: NameFormat | null;
  teacherSubject: boolean;
  captionSizes?: VignetteCaptionSizes | null;
  formatWins?: boolean;
  fromPack?: boolean;
};

function captionRules(caption: NameFormat | CaptionRules | undefined): CaptionRules {
  if (caption === undefined) return vignetteCaptionContext(undefined);
  return typeof caption === 'string' ? { nameFormat: caption, teacherNameFormat: caption, teacherSubject: true } : caption;
}

// «Как в паке» для всех: на поверхности, где побеждал бы формат, его нет, и ФИО печатает раскладка рамки, а безымянные
// строки ячейки — то, что разложил по ним дизайнер. Вне виньетки формат проекта — запасной, его ручка не трогает.
function captionFormat(teacher: boolean, rules: CaptionRules): NameFormat | null {
  if (rules.fromPack && rules.formatWins) return null;
  return teacher ? rules.teacherNameFormat : rules.nameFormat;
}

/** Формат панели, которым печатается ФИО роли; `null` — печатается своя раскладка рамки. */
export function winningNameFormat(teacher: boolean, caption: NameFormat | CaptionRules): NameFormat | null {
  const rules = captionRules(caption);
  const format = captionFormat(teacher, rules);
  return rules.formatWins && format ? format : null;
}

type PhotoBookModel = Record<string, unknown> | object | null | undefined;

/** Ростерная ли рамка — единственное, что обход спрашивает у поверхности о её составе. */
type RosterCellCheck = (group: Record<string, unknown>) => boolean;

/**
 * Обход поверхности этот вопрос не задаёт: ответ приходит сверху одной и той же функцией в гидрацию и в
 * вырезание. Вход снимается вместе с мостом, когда роль `owner` начнёт отвечать на него сама.
 */
function surfaceIsRoster(spread: Record<string, unknown>): boolean {
  return isRosterSurface(spread as { spread_type?: number });
}

function rosterCellsOf(surface: RosterSurface, rosterSurface: boolean): RosterCellCheck {
  const isRosterCell = rosterCellPredicate(surface, rosterSurface);
  return group => isRosterCell(group as unknown as RosterCellLike);
}

/** Твин `rosterCellsOf` на форме боксов: контейнер другой, мерка ячейки — та же, из ядра. */
function rosterCellsBoxesOf(surface: SpreadBoxesLike, rosterSurface: boolean): RosterCellCheck {
  const isRosterCell = rosterCellPredicateBox(surface, rosterSurface);
  return group => isRosterCell(group as unknown as CustomGroupTemplateBox);
}

type CaptionSurfaceRules = Pick<SurfaceCaptionRules, 'formatWins' | 'rosterCellsFormatWins'>;

/**
 * Подписи каких ячеек пак подгоняет под образец: ячейка ростера с персоной на виньетке и карточка класса на обложке.
 * Одна мерка у подстановки и у ресайза (`refitPackCaptionsAfterRescale`): прочие тексты оба держат как были.
 */
function fitsCaptionToSample(rules: CaptionSurfaceRules, isRosterCell: RosterCellCheck, group: Record<string, unknown>): boolean {
  return (
    (rules.formatWins === true || rules.rosterCellsFormatWins === true) &&
    isRosterCell(group) &&
    groupCarriesPerson(group as unknown as DrawableGroupData)
  );
}

/** Мерка {@link fitsCaptionToSample} для поверхности книги: обложка — как в `applyPersonsToVignetteModel`. */
export function packCaptionCellsOf(surface: object, cover: boolean): (group: object) => boolean {
  const rosterSurface = !cover && surfaceIsRoster(surface as Record<string, unknown>);
  const isRosterCell = rosterCellsOf(surface as RosterSurface, rosterSurface);
  const rules: CaptionSurfaceRules = { formatWins: rosterSurface, rosterCellsFormatWins: cover || rosterSurface };
  return group => fitsCaptionToSample(rules, isRosterCell, group as Record<string, unknown>);
}

/** Где печать читает формат панели вместо раскладки рамки: вся виньетка, на обложке — ячейки ростера. */
export function captionFormatWinsBox(
  surface: SpreadBoxesLike & { readonly spread_type?: number },
  cover: boolean,
  group: CustomGroupTemplateBox | null | undefined,
): boolean {
  const rosterSurface = !cover && surfaceIsRoster(surface as Record<string, unknown>);
  if (rosterSurface) return true;
  return cover && !!group && rosterCellsBoxesOf(surface, rosterSurface)(group as unknown as Record<string, unknown>);
}

/** Правила подстановки, зависящие от настроек проекта, а не от разворота. */
export interface PersonTextOptions {
  /**
   * Гейт-режим «как напечатается»: незаполненное поле персоны резолвится в '' вместо слова-заглушки
   * («Имя», «Фамилия», «Отчество», «Предмет») — заглушка на бумаге брак, и гейт пустых текстов обязан
   * его увидеть. ФИО пустеет целиком, когда пуста любая обязательная часть (имя или фамилия).
   * Рендер этот флаг не поднимает никогда; позиционные слоты судятся тем же законом.
   */
  strictBlanks?: boolean;
  /**
   * Утверждённый альбом заморожен: цитаты рамок (персоны, классрука вместе с его подписью, строки `leadLine`) печатаются
   * как сохранены — без подстановки из персон, гашения выключенных групп и посадки. Имена ячеек, предметы и фото — как обычно.
   * `'all'` — утверждение материализовало подстановку (`vignetteQuotesFrozen`): пустая рамка печатается пустой.
   * `true` — утверждён до материализации: рамка с пустым сохранённым текстом заполняется, гасится и садится как живая.
   */
  frozenQuotes?: boolean | 'all';
}

/**
 * Ячейка ростера, посаженная на персону, которой в списке НЕТ. Гидрация такую рамку пропускает молча,
 * и на бумаге это два РАЗНЫХ симптома: пустая рамка уезжает дырой, а рамка с запечённым кадром печатает
 * фото и ФИО удалённого ученика.
 */
export interface OrphanRosterCell {
  readonly personId: string;
  readonly cellId?: string;
  readonly peopleType?: string;
  /** Кадр уже лежит в модели: ни гидрация (персоны нет), ни вырезание на сохранении его не трогают. */
  readonly bakedPhoto: boolean;
}

export interface ApplyPersonsOptions extends PersonTextOptions {
  injectPhotos?: boolean;
  /**
   * Диагностика осиротевших привязок. Библиотека логгера не знает — сообщение уходит коллбеком, как у
   * декодера модели (`decodePersistedBookModel(model, onIssue)`); кто вызвал, тот и решает, куда писать.
   */
  onOrphanRosterCell?: (cell: OrphanRosterCell) => void;
  /** Переключатели цитат групп (`resolveQuoteGroups`): выключенная группа цитату на виньетке не печатает. */
  quoteGroups?: VignetteQuoteGroupStates | null;
  /** Цитаты проекта (`photoBook.quotes`): личные потолки `limitByPerson` режут цитату рамки серии. */
  quotes?: QuotesSettings | null;
}

/**
 * Derives NameFormat from settings, providing backwards-compat for legacy `reverseNameOrder` boolean.
 * Call at the settings boundary; do not propagate the raw boolean further.
 */
export function deriveNameFormat(nameFormat: NameFormat | undefined, reverseNameOrder: boolean | undefined): NameFormat {
  if (nameFormat !== undefined) return nameFormat;
  return reverseNameOrder === true ? NameFormat.FIRST_PATRONYMIC_SURNAME : NameFormat.SURNAME_FIRST_PATRONYMIC;
}

/**
 * Применяет данные персон ко ВСЕМ поверхностям фотокниги — обложке и каждому развороту, каким бы ни был
 * его тип. Судьбу рамки решает роль её группы: ростерная ячейка берёт кадр и ФИО из карточки персоны,
 * роль владельца — только текст, `free` и рамка без роли не трогаются.
 *
 * @param model - The photobook model (BookDTO)
 * @param persons - Current persons list with up-to-date names
 * @param caption - Подписи проекта; на виньетке формат роли побеждает `fioLayout` бокса, вне виньетки — нет
 * @param ownerPersonId - ID of the album-owner person (OWNER-group and group-less text substitution)
 * @param options - { injectPhotos: true } injects vignette photo/avatar; false = names-only (save mode)
 */
export function applyPersonsToVignetteModel<T extends PhotoBookModel>(
  model: T,
  persons: Person[] | null | undefined,
  // Голый NameFormat — прежние вызовы до перехода на контекст: один формат на обе роли.
  caption?: VignetteCaptionContext | NameFormat,
  ownerPersonId?: string | null,
  options: ApplyPersonsOptions = { injectPhotos: true },
): T {
  if (!model || !persons || persons.length === 0) return model;

  const nameFormat = captionRules(caption);
  const personsMap = new Map(persons.map(p => [p.id, p]));
  const modelRecord = model as Record<string, unknown>;

  const resolvedOwnerId = ownerPersonId ?? (modelRecord['personId'] as string | undefined);
  const owner = resolvedOwnerId ? personsMap.get(resolvedOwnerId) : undefined;

  const coverRaw = modelRecord['cover'];
  const updatedCover =
    coverRaw && typeof coverRaw === 'object'
      ? applyPersonsToSurface(coverRaw as unknown as RosterSurface, personsMap, nameFormat, owner, options, false, true)
      : coverRaw;

  return {
    ...modelRecord,
    ...(coverRaw !== undefined && { cover: updatedCover }),
    spreads: Array.isArray(modelRecord['spreads'])
      ? modelRecord['spreads']
          .map((spread: Record<string, unknown>) =>
            applyPersonsToSurface(
              spread as unknown as RosterSurface,
              personsMap,
              nameFormat,
              owner,
              options,
              surfaceIsRoster(spread),
            ),
          )
          .map((spread, index) => {
            const passed = fitVignetteQuotes(
              blankOffGroupQuotes(spread as unknown as Record<string, unknown>, options.quoteGroups, owner?.id),
              options.quotes,
              owner?.id,
            );
            return options.frozenQuotes
              ? keepFrozenBoxes(passed, spread, (modelRecord['spreads'] as unknown[])[index], options.frozenQuotes)
              : passed;
          })
      : modelRecord['spreads'],
  } as T;
}

/**
 * Вход сужен до `Pick<SpreadData, 'collages'>`: обход физически не может спросить, что это за разворот,
 * — только из чего он состоит. Обложка и любой разворот проходят здесь одним и тем же путём.
 */
function applyPersonsToSurface<S extends RosterSurface>(
  surface: S,
  personsMap: Map<string, Person>,
  nameFormat: CaptionRules,
  owner: Person | undefined,
  options: ApplyPersonsOptions,
  rosterSurface: boolean,
  // Карточки класса на обложке (педагоги) подписываются форматом панели виньетки, как и на самой виньетке.
  rosterCellsFormatWins = rosterSurface,
): S {
  if (!Array.isArray(surface.collages)) return surface;

  const isRosterCell = rosterCellsOf(surface, rosterSurface);
  const rules: SurfaceCaptionRules = { ...nameFormat, formatWins: rosterSurface, rosterCellsFormatWins };
  const collages = surface.collages as unknown as Record<string, unknown>[];
  // Поле под ячейками нужно только кеглю класса: он есть лишь у ячеек виньетки.
  const gapsBelow = rosterSurface && nameFormat.captionSizes ? gapsBelowCells(collages, isRosterCell) : NO_GAPS;
  return {
    ...surface,
    collages: collages.map(collage => applyPersonsToCollage(collage, personsMap, rules, owner, options, isRosterCell, gapsBelow)),
  } as S;
}

/** Правила подписи поверхности: вне виньетки ячейка класса может держать формат виньетки, а прочие тексты — нет. */
type SurfaceCaptionRules = CaptionRules & { rosterCellsFormatWins?: boolean };

function applyPersonsToCollage(
  collage: Record<string, unknown>,
  personsMap: Map<string, Person>,
  nameFormat: SurfaceCaptionRules,
  owner: Person | undefined,
  options: ApplyPersonsOptions,
  isRosterCell: RosterCellCheck,
  gapsBelow: ReadonlyMap<object, number>,
): Record<string, unknown> {
  if (!Array.isArray(collage['drawableObjects'])) return collage;

  return {
    ...collage,
    drawableObjects: collage['drawableObjects'].map((obj: Record<string, unknown>) =>
      applyPersonsToDrawable(obj, personsMap, nameFormat, owner, options, isRosterCell, gapsBelow.get(obj)),
    ),
  };
}

function applyPersonsToDrawable(
  obj: Record<string, unknown>,
  personsMap: Map<string, Person>,
  nameFormat: SurfaceCaptionRules,
  owner: Person | undefined,
  options: ApplyPersonsOptions,
  isRosterCell: RosterCellCheck,
  gapBelow?: number,
): Record<string, unknown> {
  if (obj['type'] === 'group') {
    return applyPersonsToDrawableObject(obj, personsMap, nameFormat, owner, options, isRosterCell, gapBelow);
  }

  if (obj['type'] !== 'text') return obj;
  const textType = obj['textType'];
  if (textType === VignetteTextTypeEnum.LEAD_QUOTE || textType === VignetteTextTypeEnum.LEAD_SIGNATURE) {
    if (holdsFrozenText(obj, options.frozenQuotes)) return obj;
    return applyLeadTextBox(obj, textType, personsMap, options.quotes);
  }

  return applyOwnerTextBox(obj, owner, nameFormat, options);
}

const LEAD_SIGNATURE_FIO: FioLayout = { order: 'givenFirst', lineBreak: 'single', includePatronymic: true };

/** Ведущий виньетки — тем же законом, что `pickTeacherLayoutTemplate`: `isLead` педагог, иначе единственный. */
export function vignetteLead(persons: Iterable<Person>): Person | undefined {
  const teachers = [...persons].filter(isTeacher);
  const lead = teachers.find(
    teacher => (teacher as Teacher).isLead === true || (teacher.type as string) === FC_PersonTypeEnum.CLASS_TEACHER,
  );
  return lead ?? (teachers.length === 1 ? teachers[0] : undefined);
}

const NBSP = '\u00A0';

/**
 * Строка цитаты классного руководителя (R427, рамка с меткой `leadLine`): «цитата — Имя Отчество», подпись склеена
 * неразрывными пробелами и переносится целиком. Цитаты нет — пусто.
 */
function leadLineText(lead: Person, quote: string): string {
  const quoteText = quote.trim();
  if (!quoteText) return '';
  return `${quoteText}${leadSignatureTail(lead)}`;
}

/** Хвост подписи строки классрука: тире и «Имя Отчество» на неразрывных пробелах; посадка цитаты его не режет. */
function leadSignatureTail(lead: Person): string {
  const signature = [(lead.name ?? '').trim(), isTeacher(lead) ? ((lead as Teacher).fatherName ?? '').trim() : '']
    .filter(Boolean)
    .join(NBSP);
  return signature ? `${NBSP}—${NBSP}${signature}` : '';
}

/**
 * Запас строки классрука под подпись: подпись неразрывна — не влезла в конец строки, уходит целиком на следующую
 * и теряет до своей длины в конце прежней, поэтому из ёмкости она вычтена дважды. Его же вычитают потолки ведущего.
 */
export const leadLineQuoteReserve = (lead: Person): number => 2 * leadSignatureTail(lead).length;

/** Цитата и подпись классрука вне ячейки (R22): читают ведущего, владельца альбома не трогают. */
function applyLeadTextBox(
  obj: Record<string, unknown>,
  textType: VignetteTextTypeEnum,
  personsMap: Map<string, Person>,
  quotes: QuotesSettings | null | undefined,
): Record<string, unknown> {
  const lead = vignetteLead(personsMap.values());
  if (!lead) return obj;

  const text =
    textType === VignetteTextTypeEnum.LEAD_QUOTE ? lead.description || '' : formatFullNameWithLayout(lead, LEAD_SIGNATURE_FIO);
  const leadLine = textType === VignetteTextTypeEnum.LEAD_QUOTE && (obj as Pick<DrawableTextData, 'leadLine'>).leadLine === true;
  if (leadLine && (obj as Pick<DrawableTextData, 'vignetteQuote'>).vignetteQuote === true) {
    // Посадка (R430) — по одной цитате, подпись приклеивается после и не режется никогда.
    const tail = leadSignatureTail(lead);
    const fitted = fitVignetteQuote(obj, text, vignetteQuoteLimit(quotes, lead.id), leadLineQuoteReserve(lead));
    const quoteText = (fitted['text'] as { text: string }).text;
    const line = quoteText ? `${quoteText}${tail}` : '';
    return { ...fitted, text: { ...(fitted['text'] as Record<string, unknown>), text: line } };
  }

  if (leadLine) {
    return { ...obj, text: { ...(obj['text'] as Record<string, unknown> | undefined), text: leadLineText(lead, text) } };
  }

  return { ...obj, text: { ...(obj['text'] as Record<string, unknown> | undefined), text } };
}

/**
 * ПЕРЕХОДНОЕ. Безгрупповая PERSON_*-рамка роли не носит, и владелец — единственная персона, которую к
 * ней можно привязать. Держится для ВСЕХ поверхностей: на виньетке такие рамки ТОЖЕ есть (пункт «Сменить
 * тип» альбом-редактора размечал верхнеуровневый текст персональным типом), и ответ обязан быть один —
 * иначе имя владельца печатается, а редактор про него не знает.
 * Снимается после миграции, когда счётчик безгрупповых PERSON_*-рамок сойдётся в ноль.
 */
function applyOwnerTextBox(
  obj: Record<string, unknown>,
  owner: Person | undefined,
  nameFormat: CaptionRules,
  options: ApplyPersonsOptions,
): Record<string, unknown> {
  // Без владельца подставлять в текст нечего — бокс остаётся как есть.
  if (!owner) return obj;

  const textType = obj['textType'] as string | undefined;
  if (!isPersonTextField(textType) || holdsFrozenText(obj, options.frozenQuotes)) return obj;

  const fioLayout = obj['fioLayout'] as FioLayout | undefined;
  const resolvedText = resolveTextByType(owner, textType, nameFormat, fioLayout, options);
  if (resolvedText === undefined) return obj;

  const existingText = obj['text'] as Record<string, unknown> | undefined;
  return {
    ...obj,
    text: { ...existingText, text: resolvedText },
  };
}

/** Ручной кроп пользователя валиден только с числовым zoom; иначе действует авто-посадка по лицам. */
function toVignettePlacement(transform: Person['vignetteTransform']): VignettePlacement | undefined {
  if (!transform || typeof transform.zoom !== 'number') return undefined;
  return { zoom: transform.zoom, focusX: transform.focusX ?? 0.5, focusY: transform.focusY ?? 0.5 };
}

function resolveVignetteSource(person: Person): Person['vignetteImage'] {
  return person.vignetteImage;
}

/**
 * АРХИТЕКТУРНОЕ РЕШЕНИЕ (ADR-0018): в виньеточной карточке значима ТОЛЬКО ссылка на ученика
 * (params.personId/peopleType). Фото, кроп и зеркало живут в person.vignetteImage +
 * person.vignetteTransform, и каждая гидрация пересчитывает плейсмент оттуда БЕЗУСЛОВНО — даже когда
 * в drawable уже стоит то же самое фото с готовым кадром.
 *
 * Читатель здесь — единственный страж инварианта: сервер модель не чистит и записать в неё фото никто
 * не мешает. Поэтому всё, что лежит в drawable, считается мусором и игнорируется молча: оно могло
 * остаться от прежнего владельца карточки (свап/каскад), от старого клиента или от книги, сохранённой
 * до появления этого правила. НИКОГДА не добавляй сюда ветку «фото уже стоит — оставим как есть»:
 * ровно так печать однажды и заморозила кадр на состоянии двухмесячной давности.
 *
 * Единственное исключение — персона без vignetteImage: подставить нечего, и старое содержимое рамки
 * остаётся нетронутым (ранний выход ниже). Это сознательно: показать устаревшее фото лучше, чем дыру.
 *
 * Индивидуальная модель внутри виньетки может появиться в будущем вместе с индивидуальными
 * виньетками — тогда у неё будет собственное поле; пока в карточке значима только ссылка.
 */
function applyVignetteImageToGroup(drawableObjects: Record<string, unknown>[], person: Person): Record<string, unknown>[] {
  const source = resolveVignetteSource(person);
  if (!source) return drawableObjects;

  const transform = person.vignetteTransform;
  const flipHorizontal = transform?.flipHorizontal;
  const flipVertical = transform?.flipVertical;
  const placement = toVignettePlacement(transform);

  return drawableObjects.map(child => {
    if (child['type'] !== 'image') return child;

    const naturalAligns = child['naturalAligns'] as Record<string, unknown> | undefined;
    const container = naturalAligns?.['container'] as { width: number; height: number } | undefined;
    const baseImage = naturalAligns?.['image'] as Record<string, unknown> | undefined;

    // Preserve the original rect angle — a missing angle makes the renderer produce NaN geometry.
    const imageAlign = container
      ? {
          angle: 0,
          ...baseImage,
          ...computeVignetteImagePlacement(container, {
            width: source.width,
            height: source.height,
            faces: source.faces,
            personBboxes: source.personBboxes,
            placement,
          }),
        }
      : baseImage;

    const hadFlip = child['flipHorizontal'] !== undefined;
    const hadFlipVertical = child['flipVertical'] !== undefined;

    return {
      ...child,
      image: source,
      ...((flipHorizontal !== undefined || hadFlip) && { flipHorizontal: flipHorizontal ?? false }),
      ...((flipVertical !== undefined || hadFlipVertical) && { flipVertical: flipVertical ?? false }),
      naturalAligns: { ...naturalAligns, image: imageAlign },
    };
  });
}

/**
 * Закон вырезания — один на обе персист-формы: снимаем кадр ТОЛЬКО там, где доказано, что гидрация
 * вернёт его из карточки персоны. Список персон не передан — доказательства нет ни на одну ячейку, а
 * вернуть кадр будет нечем: доказательства возврата у вырезания нет.
 * Ранний выход на непереданном списке — сам закон, а не оптимизация хвоста: он переживёт смену `resolveGroupPerson`.
 */
function stripLawFor(
  model: Record<string, unknown>,
  persons: readonly Person[] | null | undefined,
  ownerPersonId: string | null | undefined,
): (group: Record<string, unknown>, isRosterCell: RosterCellCheck) => boolean {
  const personsMap = persons === undefined ? null : new Map((persons ?? []).map(p => [p.id, p]));
  const resolvedOwnerId = ownerPersonId ?? (model['personId'] as string | undefined);
  const owner = personsMap && resolvedOwnerId ? personsMap.get(resolvedOwnerId) : undefined;

  return (group, isRosterCell) => {
    if (!isRosterCell(group)) return false;
    if (!personsMap) return false;
    const person = resolveGroupPerson(group, personsMap, owner);
    return !!person && !!resolveVignetteSource(person);
  };
}

/**
 * Снимает с ячеек ростера кадр, который гидрация поставит обратно из карточки персоны: в сохранённой
 * книге он лишний. Какая ячейка доказана возвратом — решает {@link stripLawFor}, один на обе формы.
 */
export function stripVignettePhotos<T extends PhotoBookModel>(
  model: T,
  persons?: readonly Person[] | null,
  ownerPersonId?: string | null,
): T {
  if (!model) return model;
  const modelRecord = model as Record<string, unknown>;

  const shouldStrip = stripLawFor(modelRecord, persons, ownerPersonId);

  const stripCollage = (collage: Record<string, unknown>, isRosterCell: RosterCellCheck): Record<string, unknown> => {
    if (!Array.isArray(collage['drawableObjects'])) return collage;
    return {
      ...collage,
      drawableObjects: (collage['drawableObjects'] as Record<string, unknown>[]).map(obj => {
        if (obj['type'] !== 'group') return obj;
        if (!shouldStrip(obj, isRosterCell)) return obj;
        const children = obj['drawableObjects'] as Record<string, unknown>[] | undefined;
        if (!Array.isArray(children)) return obj;
        return {
          ...obj,
          drawableObjects: children.map(child => {
            if (child['type'] !== 'image') return child;
            return { ...child, image: null };
          }),
        };
      }),
    };
  };

  // Вырезание меряет ячейки той же меркой и тем же мостом, что и гидрация: иначе кадр не вернётся.
  const stripSurface = (surface: Record<string, unknown>, rosterSurface: boolean): Record<string, unknown> => {
    if (!Array.isArray(surface['collages'])) return surface;
    const isRosterCell = rosterCellsOf(surface as unknown as RosterSurface, rosterSurface);
    return {
      ...surface,
      collages: (surface['collages'] as Record<string, unknown>[]).map(collage => stripCollage(collage, isRosterCell)),
    };
  };

  const stripSpread = (spread: Record<string, unknown>): Record<string, unknown> => stripSurface(spread, surfaceIsRoster(spread));

  const coverRaw = modelRecord['cover'] as Record<string, unknown> | undefined;
  const strippedCover = coverRaw ? stripSurface(coverRaw, false) : coverRaw;

  return {
    ...modelRecord,
    ...(coverRaw !== undefined && { cover: strippedCover }),
    spreads: Array.isArray(modelRecord['spreads'])
      ? (modelRecord['spreads'] as Record<string, unknown>[]).map(stripSpread)
      : modelRecord['spreads'],
  } as T;
}

/**
 * Твин {@link stripVignettePhotos} для персист-формы custom-template-v1 (S9-стор альбома): та же мерка
 * ячеек (ростерная роль минус якорь владельца), тот же охват (обложка + развороты), только контейнер —
 * `boxes`, а вырезанный кадр — `params.assignedPhoto: null` без placement-прямоугольника (ровно то, что
 * кодек персистит для v0-ячейки со снятым кадром).
 */
export function stripVignettePhotosBoxes<T extends PhotoBookModel>(
  model: T,
  persons?: readonly Person[] | null,
  ownerPersonId?: string | null,
): T {
  if (!model) return model;
  const modelRecord = model as Record<string, unknown>;

  const shouldStrip = stripLawFor(modelRecord, persons, ownerPersonId);

  const stripCollage = (collage: Record<string, unknown>, isRosterCell: RosterCellCheck): Record<string, unknown> => {
    if (!Array.isArray(collage['boxes'])) return collage;
    return {
      ...collage,
      boxes: (collage['boxes'] as Record<string, unknown>[]).map(box => {
        if (box['type'] !== 'group') return box;
        if (!shouldStrip(box, isRosterCell)) return box;
        const children = box['boxes'] as Record<string, unknown>[] | undefined;
        if (!Array.isArray(children)) return box;
        return {
          ...box,
          boxes: children.map(child => {
            if (child['type'] !== 'image') return child;
            const params: Record<string, unknown> = { ...(child['params'] as Record<string, unknown>), assignedPhoto: null };
            delete params['photoPlacementRect'];
            return { ...child, params };
          }),
        };
      }),
    };
  };

  const stripSurface = (surface: Record<string, unknown>, rosterSurface: boolean): Record<string, unknown> => {
    if (!Array.isArray(surface['collages'])) return surface;
    const isRosterCell = rosterCellsBoxesOf(surface as SpreadBoxesLike, rosterSurface);
    return {
      ...surface,
      collages: (surface['collages'] as Record<string, unknown>[]).map(collage => stripCollage(collage, isRosterCell)),
    };
  };

  const stripSpread = (spread: Record<string, unknown>): Record<string, unknown> => stripSurface(spread, surfaceIsRoster(spread));

  const coverRaw = modelRecord['cover'] as Record<string, unknown> | undefined;
  const strippedCover = coverRaw ? stripSurface(coverRaw, false) : coverRaw;

  return {
    ...modelRecord,
    ...(coverRaw !== undefined && { cover: strippedCover }),
    spreads: Array.isArray(modelRecord['spreads'])
      ? (modelRecord['spreads'] as Record<string, unknown>[]).map(stripSpread)
      : modelRecord['spreads'],
  } as T;
}

/** Группа альбомного ВЛАДЕЛЬЦА: и `owner`, и кадр «группа со владельцем» ведут к нему одному. */
export function isOwnerGroupPeopleType(peopleType: string | undefined): boolean {
  return peopleType === 'owner' || peopleType === 'groupWithOwner';
}

/** Одна трактовка «чья ячейка» для инъекции и вырезания: иначе strip удалит фото, которое не вернётся. */
export function resolveGroupPerson(
  group: Record<string, unknown>,
  personsMap: Map<string, Person>,
  owner: Person | undefined,
): Person | undefined {
  const params = group['params'] as Record<string, unknown> | undefined;
  const peopleType = params?.['peopleType'] as string | undefined;
  const personId = params?.['personId'] as string | undefined;

  if (isOwnerGroupPeopleType(peopleType)) return owner;
  return personId ? personsMap.get(personId) : undefined;
}

/**
 * Привязка, за которой персоны уже нет, — единственный симптом потери ученика, который видно из модели.
 * Считается ТОЛЬКО с ростерной ячейки: рамка владельца и рамка без привязки кадра не ждут, а сообщение
 * о них было бы шумом. Пустая рассадка сюда не доходит вовсе — гидрация без персон не запускается.
 */
function reportOrphanRosterCell(obj: Record<string, unknown>, options: ApplyPersonsOptions, isRosterCell: RosterCellCheck): void {
  if (!options.onOrphanRosterCell || !isRosterCell(obj)) return;

  const params = obj['params'] as Record<string, unknown> | undefined;
  const personId = params?.['personId'] as string | undefined;
  if (!personId) return;

  options.onOrphanRosterCell({
    personId,
    cellId: obj['id'] as string | undefined,
    peopleType: params?.['peopleType'] as string | undefined,
    bakedPhoto: hasBakedPhoto(obj),
  });
}

// Кадр в модели решает, ЧТО напечатается: пустая рамка — дыра, запечённая — данные удалённого ученика.
function hasBakedPhoto(group: Record<string, unknown>): boolean {
  const children = group['drawableObjects'] as Record<string, unknown>[] | undefined;
  if (!Array.isArray(children)) return false;
  return children.some(child => child['type'] === 'image' && !!child['image']);
}

const PACK_SAMPLE_WORDS: ReadonlySet<string> = new Set([...POSITIONAL_PERSON_TEXT_TYPES].map(type => textPlaceholderFor(type)));

const isPersonRoleText = (textType: unknown): boolean =>
  textType === TextTypeEnum.PERSON_FULL_NAME || POSITIONAL_PERSON_TEXT_TYPES.has(textType as string);

// Слово-заглушка или образец PSD в слоте персоны («Александр Коновалов») — не текст альбома: пустеет.
function blankPackSample(child: Record<string, unknown>): Record<string, unknown> {
  if (child['type'] !== 'text') return child;
  const text = child['text'] as Record<string, unknown> | undefined;
  const sample =
    PACK_SAMPLE_WORDS.has(String(text?.['text'] ?? '').trim()) ||
    (isPersonRoleText(child['textType']) && isPackSampleSlot(child as unknown as DrawableTextData));
  return sample ? { ...child, text: { ...text, text: '' } } : child;
}

// Ячейку без персоны подстановка не заполняет: образец пака печатается как есть и для гейта — пустота.
function blankPackSamples(group: Record<string, unknown>): Record<string, unknown> {
  const children = group['drawableObjects'] as Record<string, unknown>[] | undefined;
  if (!Array.isArray(children)) return group;
  return { ...group, drawableObjects: children.map(blankPackSample) };
}

/**
 * Поверхность без владельца (эталон папки, альбом без ученика): рамка владельца и свободный слот персоны образец пака
 * не хранят — подсказку «Имя Фамилия» рисует редактор ({@link positionalNamePlaceholders}), в модель она не попадает.
 */
export function blankOwnerlessPersonSamples<S extends Pick<SpreadData, 'collages'>>(surface: S): S {
  const blank = (obj: Record<string, unknown>): Record<string, unknown> =>
    obj['type'] === 'group'
      ? isOwnerGroupPeopleType((obj['params'] as { peopleType?: string } | undefined)?.peopleType)
        ? blankPackSamples(obj)
        : obj
      : isPersonRoleText(obj['textType'])
        ? blankPackSample(obj)
        : obj;
  return {
    ...surface,
    collages: surface.collages.map(collage => ({
      ...collage,
      drawableObjects: (collage.drawableObjects as unknown as Record<string, unknown>[]).map(blank) as unknown as DrawableData[],
    })),
  };
}

function applyPersonsToDrawableObject(
  obj: Record<string, unknown>,
  personsMap: Map<string, Person>,
  surfaceRules: SurfaceCaptionRules,
  owner: Person | undefined,
  options: ApplyPersonsOptions,
  isRosterCell: RosterCellCheck,
  // Белое поле под ячейкой до соседа снизу (`gapsBelowCells`); не знаем — граница подписей сама ячейка.
  gapBelow?: number,
): Record<string, unknown> {
  if (obj['type'] !== 'group') return obj;
  const nameFormat: CaptionRules =
    surfaceRules.rosterCellsFormatWins && isRosterCell(obj) ? { ...surfaceRules, formatWins: true } : surfaceRules;

  const person = resolveGroupPerson(obj, personsMap, owner);
  if (!person) {
    reportOrphanRosterCell(obj, options, isRosterCell);
    return options.strictBlanks && groupCarriesPerson(obj as unknown as DrawableGroupData) ? blankPackSamples(obj) : obj;
  }

  const ownObjects = obj['drawableObjects'] as Record<string, unknown>[] | undefined;
  if (!Array.isArray(ownObjects)) return obj;
  // Кегль класса ложится на ячейку ростера виньетки ДО раскладки подписи: перенос и ужим судят уже его. Карточку
  // серии цитат раскладывает генератор — кегль класса он уже взял, подстановка её подпись не перекладывает.
  const laidByClassSize = surfaceRules.formatWins === true && isRosterCell(obj) && !isQuoteCard(ownObjects);
  const sizes = laidByClassSize ? nameFormat.captionSizes : undefined;
  const classSized = !!(isTeacher(person) ? sizes?.teacher : sizes?.student);
  const drawableObjects = sizes ? withClassCaptionSizes(ownObjects, sizes, isTeacher(person)) : ownObjects;

  const { photo, fits, boxes } = captionInputsOf(drawableObjects);
  const { slots, oneLine, split, resolved } = planPersonCaptions(boxes, person, nameFormat, photo?.width, options);
  // Второй бокс однострочного формата пуст по замыслу: строгий прогон гейта скрывает его, а не судит.
  const designBlankSlots = options.strictBlanks && oneLine ? slots : [];
  // Подгонка под образец идёт с форматом панели: карточка класса на обложке ужимает длинную подпись так же,
  // как ячейка виньетки, иначе «Классный руководитель» уезжает под соседнюю карточку.
  const fitToSample = fitsCaptionToSample(surfaceRules, isRosterCell, obj);
  let textIndex = 0;

  const withTexts = drawableObjects.map(child => {
    if (child['type'] !== 'text') return child;

    const index = textIndex++;
    const next = resolved[index];
    // Только `undefined` значит «не трогать бокс»; '' — подстановка пустого поля персоны, её надо применить,
    // иначе в боксе останется образец из дизайн-пака (помечен TemplateTextOrigin.SAMPLE).
    if (next === undefined || holdsFrozenText(child, options.frozenQuotes)) return child;
    if (designBlankSlots[index] === 1) return { ...child, visible: false };

    const existingText = child['text'] as Record<string, unknown> | undefined;
    const fit = fits[index];
    const filled = { ...child, text: { ...existingText, text: next } };
    const previous = String(existingText?.['text'] ?? '');
    // Ужимает строку переноса только пак, и к кеглю дизайна возвращаются только эти два бокса.
    const nameLine = slots[index] === 0 || slots[index] === 1;
    const design = nameLine ? withDesignSize(filled, fit) : filled;
    const fitted = !fitToSample
      ? design
      : isRoleCaption(child)
        ? wrapCaptionToSample(filled, previous)
        : fit.sampleFit
          ? fitCaptionToSample(filled, previous, photo)
          : design;
    // Движок autoFit не рисует: строка переноса любого бокса ужимается до ширины подписи здесь; кегль руки — нет.
    // На кегле класса так же ужимается строка со словом, что не влезло в ширину подписи и отдельной строкой; одно имя
    // держит один кегль на все свои строки (`withOneNameSize`).
    return nameLine && !fit.hand && ((!oneLine && split) || classSized) ? shrinkToCaption(fitted, photo) : fitted;
  });
  const lifted = liftRoleIntoBlankSlot(withTexts, slots, oneLine);
  // Формат педагога правит число строк ФИО: подпись под теми же фото растёт или сжимается, предмет — сразу под ней.
  const flowed =
    nameFormat.formatWins === true && isTeacher(person) ? flowTeacherName(lifted, drawableObjects, person, nameFormat) : lifted;
  // Строки подписей на кегле класса — по правилам типографики, когда слова уже подставлены: высоту решает текст,
  // а имя держит один кегль на все свои строки.
  const cellHeight = (obj['naturalAligns'] as { container?: { height?: number } } | undefined)?.container?.height;
  const subjected = sizes?.subject && isTeacher(person) ? withSubjectToWidth(flowed, photo) : flowed;
  const named = drawableObjects !== ownObjects && classSized ? withOneNameSize(subjected) : subjected;
  const laidOut = drawableObjects !== ownObjects ? layCaptionLeading(named, captionLimitOf(named, cellHeight, gapBelow)) : flowed;

  return {
    ...obj,
    // Фото — только в ячейку ростера; роль `owner` кадра не получает нигде, его выбирает пользователь.
    drawableObjects: options.injectPhotos && isRosterCell(obj) ? applyVignetteImageToGroup(laidOut, person) : laidOut,
  };
}

/**
 * Карточка серии цитат Люмир: ФИО, которое не переносится (карточка «снизу», `fioLayout.keepLine`), или подпись сбоку
 * от фото рядом с рамкой цитаты серии (`vignetteQuote`). Её подпись генератор кладёт кеглем класса по своей мерке «как
 * напечатано» и ставит цитату строкой ниже подогнанного ФИО — перекладка подстановкой сдвинула бы цитату. Блок
 * владельца базовой сетки (ФИО под фото, цитата сбоку) генератор кеглем класса не кладёт — его ведёт подстановка.
 */
function isQuoteCard(children: readonly Record<string, unknown>[]): boolean {
  const texts = textChildren(children);
  if (texts.some(text => (text['fioLayout'] as { keepLine?: boolean } | undefined)?.keepLine === true)) return true;
  if (!texts.some(text => text['vignetteQuote'] === true)) return false;
  const photo = photoOf(children);
  return texts.some(text => text['vignetteQuote'] !== true && besidePhoto(spanOf(text), photo));
}

type BoxTop = { naturalAligns?: { container?: { top?: number; height?: number } } };
const boxTop = (box: Record<string, unknown> | undefined): number | undefined =>
  (box as BoxTop | undefined)?.naturalAligns?.container?.top;
const boxHeight = (box: Record<string, unknown> | undefined): number | undefined =>
  (box as BoxTop | undefined)?.naturalAligns?.container?.height;

function withTop(box: Record<string, unknown>, top: number): Record<string, unknown> {
  const aligns = (box as BoxTop).naturalAligns;
  return { ...box, naturalAligns: { ...aligns, container: { ...aligns?.container, top } } };
}

const CAPTION_QUOTE_TYPES: ReadonlySet<string> = new Set([TextTypeEnum.PERSON_QUOTE, VignetteTextTypeEnum.LEAD_QUOTE]);

type TextBoxShape = {
  naturalAligns?: { container?: { left?: number; top?: number; height?: number; width?: number }; fontSize?: number };
  text?: { text?: string };
};

const textOf = (box: Record<string, unknown> | undefined): string => String((box as TextBoxShape | undefined)?.text?.text ?? '');
const lineCount = (text: string, textLayout?: number): number => Math.max(1, authoredLines(text, textLayout).length);
/** The paragraph rule a drawable text box is laid out by (its passthrough meta), unknown — `undefined`. */
const textLayoutOf = (box: Record<string, unknown> | undefined): number | undefined =>
  (box as { templateMeta?: { textLayout?: number } } | undefined)?.templateMeta?.textLayout;

const FROZEN_TEXT_TYPES: ReadonlySet<string> = new Set([
  TextTypeEnum.PERSON_QUOTE,
  VignetteTextTypeEnum.LEAD_QUOTE,
  VignetteTextTypeEnum.LEAD_SIGNATURE,
]);

type FrozenQuotes = ApplyPersonsOptions['frozenQuotes'];

/** Рамка, которую заморозка держит: цитата или подпись — при `'all'` любая, при `true` только с сохранённым текстом. */
const holdsFrozenText = (box: Record<string, unknown>, frozen: FrozenQuotes): boolean =>
  !!frozen && FROZEN_TEXT_TYPES.has(box['textType'] as string) && (frozen === 'all' || textOf(box).trim() !== '');

/** Без метки — утверждение до материализации подстановки: пустые рамки по-прежнему живые. */
export function vignetteQuotesFreeze(
  book: { status: string; generationInfo?: { vignetteQuotesFrozen?: true } | null } | null | undefined,
): false | true | 'all' {
  if (book?.status !== 'approved') return false;
  return book.generationInfo?.vignetteQuotesFrozen === true ? 'all' : true;
}

/**
 * Гашение и посадка идут по развороту целиком, замороженная рамка возвращается из заполненного разворота: живые
 * рамки судятся как всегда. Подстановка и проходы структуру не меняют — три дерева идут по одним индексам.
 */
function keepFrozenBoxes(passed: unknown, filled: unknown, stored: unknown, frozen: FrozenQuotes): unknown {
  if (passed === filled || !passed || typeof passed !== 'object') return passed;
  if (Array.isArray(passed)) {
    return passed.map((node, i) =>
      keepFrozenBoxes(node, (filled as unknown[])[i], (stored as unknown[] | undefined)?.[i], frozen),
    );
  }

  const storedNode = (stored ?? {}) as Record<string, unknown>;
  if (holdsFrozenText(storedNode, frozen)) return filled;
  const node = passed as Record<string, unknown>;
  const key = Array.isArray(node['collages']) ? 'collages' : 'drawableObjects';
  if (!Array.isArray(node[key])) return passed;
  return { ...node, [key]: keepFrozenBoxes(node[key], (filled as Record<string, unknown>)[key], storedNode[key], frozen) };
}

/**
 * ФИО педагога одним боксом с раскладкой (`fioLayout`) и предмет под ним: бокс ФИО — на столько строк, сколько их
 * у подписи, предмет и всё, что ниже его, — сдвигаются на разницу; фото и ячейка на месте. Шаг строки — тот, на
 * который бокс был сложен для прежней подписи; кегль ужимается, только когда строка не влезает в ширину подписи
 * (та же, что у ученика: фото или бокс шире него).
 */
function flowTeacherName(
  children: Record<string, unknown>[],
  before: Record<string, unknown>[],
  person: Person,
  rules: CaptionRules,
): Record<string, unknown>[] {
  const nameIndex = children.findIndex(
    child => child['type'] === 'text' && child['textType'] === TextTypeEnum.PERSON_FULL_NAME && !!child['fioLayout'],
  );
  const subject = children.find(child => child['type'] === 'text' && child['textType'] === TextTypeEnum.PERSON_SUBJECT);
  const name = children[nameIndex];
  const aligns = (name as TextBoxShape | undefined)?.naturalAligns;
  const top = aligns?.container?.top;
  const height = aligns?.container?.height;
  const subjectTop = boxTop(subject);
  if (!name || top === undefined || !height || subjectTop === undefined || subjectTop < top + height - 1e-6) return children;

  const photo = photoOf(before);
  const width = captionWidthOf(name, photo) || aligns?.container?.width;
  const fontSize = aligns?.fontSize;
  const beforeSize = (before[nameIndex] as TextBoxShape | undefined)?.naturalAligns?.fontSize ?? fontSize;
  // Кегль дизайна закреплён в maxFontSize только у бокса с образцом пака; рука и бокс без образца — свой кегль.
  const sampleFit = before[nameIndex] && sampleFitOf(before[nameIndex], textOf(before[nameIndex]), photo);
  const handSet = !!sampleFit?.handSet;
  const design = sampleFit && !handSet ? sampleFit.start : fontSize;
  const pitch = height / lineCount(textOf(before[nameIndex]), textLayoutOf(before[nameIndex]));
  // Одна строка, что не влезает на кегле дизайна, переносится, как в базе: фамилия, затем имя с отчеством.
  // `keepLine` не переносится никогда: строка ужимается в ширину бокса.
  const single = textOf(name);
  const format = captionFormat(true, rules);
  const layout = { ...(name['fioLayout'] as FioLayout), ...(format ? nameFormatFioLayout(format) : {}) };
  // Кегль, заданный рукой, раскладку не меняет — как у ученика (`handSetSize`).
  const wrap =
    !layout.keepLine && !handSet && !single.includes('\n') && !!design && !!width && captionEms(single) * design > width + 1e-6;
  const text = wrap ? formatFullNameWithLayout(person, { ...layout, lineBreak: 'split' }) : single;
  // Старая подгонка ужимала кегль по образцу, не трогая высоту бокса: шаг строки сложен на кегле дизайна. Строку шире
  // бокса она ужимала ещё и по ширине, высоту и предмет — в той же доле; такую строку новое правило переносит, а не
  // ужимает, — значит, её сохранила старая подгонка, и шаг строки — дизайна × кегль / подгонка по образцу.
  const widthShrunk =
    !!sampleFit &&
    !sampleFit.handSet &&
    !sampleFit.legacy &&
    sampleFit.size < sampleFit.sampled - 0.01 &&
    text.includes('\n') &&
    !textOf(before[nameIndex]).includes('\n');
  const baseSize =
    !sampleFit || sampleFit.handSet
      ? beforeSize
      : sampleFit.legacy
        ? sampleFit.start
        : widthShrunk
          ? (sampleFit.start * sampleFit.size) / sampleFit.sampled
          : beforeSize;
  const ems = captionEms(text);
  const fitted = !handSet && design && width && ems > 0 ? Math.min(design, width / ems) : fontSize;
  const scale = baseSize && fitted ? fitted / baseSize : 1;
  const next = pitch * scale * lineCount(text, textLayoutOf(name));
  const shift = next - height;
  if (!wrap && Math.abs(shift) < 1e-6 && scale === 1 && fitted === fontSize) return children;

  return children.map((child, index) => {
    if (index === nameIndex) {
      return {
        ...child,
        ...(wrap && { text: { ...(child['text'] as Record<string, unknown>), text } }),
        naturalAligns: { ...aligns, container: { ...aligns?.container, height: next }, ...(fitted && { fontSize: fitted }) },
      };
    }

    const childTop = child['type'] === 'text' ? boxTop(child) : undefined;
    const moved = childTop !== undefined && childTop >= subjectTop - 1e-6 ? withTop(child, childTop + shift) : child;
    return child === subject ? withSubjectScale(moved, scale, widthShrunk) : moved;
  });
}

// Предмет держит долю ФИО, но не выше своего кегля дизайна: его закрепляет в maxFontSize первый ужим.
function withSubjectScale(subject: Record<string, unknown>, scale: number, widthShrunk: boolean): Record<string, unknown> {
  const aligns = (subject as TextBoxShape).naturalAligns;
  const size = aligns?.fontSize;
  const pinned = subject['maxFontSize'] as number | undefined;
  if (!size || scale === 1 || (scale > 1 && pinned === undefined && !widthShrunk)) return subject;

  const fontSize = pinned === undefined ? size * scale : Math.min(size * scale, pinned);
  const pin = pinned === undefined && scale < 1 && !subject['autoFit'] ? { maxFontSize: size } : {};
  return { ...subject, ...pin, naturalAligns: { ...aligns, fontSize } };
}

/**
 * Однострочный формат гасит второй бокс, и роль/предмет третьего слота встаёт на его место — прямо под ФИО.
 * Боксы меняются местами по стопке: верхний встаёт на верхний `top`, нижний — под него через прежний зазор. Высота
 * пустой строки на кегле класса — ноль, и обмен одними `top` сдвигал бы предмет на строку за каждую смену формата.
 * Цитата прямо под подписями едет вместе с ролью: иначе между ролью и цитатой зияет погашенная строка.
 */
function liftRoleIntoBlankSlot(
  children: Record<string, unknown>[],
  slots: (number | undefined)[],
  oneLine: boolean,
): Record<string, unknown>[] {
  const texts = children.filter(child => child['type'] === 'text');
  const blank = texts[slots.indexOf(1)];
  const role = texts[slots.indexOf(2)];
  const blankTop = boxTop(blank);
  const roleTop = boxTop(role);
  if (blankTop === undefined || roleTop === undefined || (oneLine ? roleTop <= blankTop : blankTop <= roleTop)) return children;

  // Цитата «под ролью» — ниже середины роли и не дальше двух строк от её низа; поставленную дизайнером в стороне
  // от подписей (сбоку от фото, внизу карточки) это не трогает.
  const pitch = Math.abs(roleTop - blankTop);
  const roleHeight = boxHeight(role) ?? pitch;
  const quoteTop = (child: Record<string, unknown>): number | undefined => {
    const top = CAPTION_QUOTE_TYPES.has(child['textType'] as string) ? boxTop(child) : undefined;
    return top !== undefined && top > roleTop + roleHeight / 2 && top <= roleTop + roleHeight + 2 * pitch + 1e-6
      ? top
      : undefined;
  };

  const [upper, lower] = oneLine ? [blank, role] : [role, blank];
  const upperTop = Math.min(blankTop, roleTop);
  const upperHeight = boxHeight(upper);
  const lowerHeight = boxHeight(lower);
  const stacked =
    upperHeight === undefined || lowerHeight === undefined
      ? undefined
      : upperTop + lowerHeight + (Math.max(blankTop, roleTop) - upperTop - upperHeight);
  const blankTo = oneLine ? (stacked ?? roleTop) : upperTop;
  const roleTo = oneLine ? upperTop : (stacked ?? blankTop);

  return children.map(child => {
    if (child === blank) return withTop(child, blankTo);
    if (child === role) return withTop(child, roleTo);
    const top = quoteTop(child);
    return top === undefined ? child : withTop(child, top + roleTo - roleTop);
  });
}

const longestLine = (text: string): number => Math.max(1, ...authoredLines(text).map(line => line.trim().length));

type Span = { left?: number; width?: number };
const spanOf = (box: Record<string, unknown> | undefined): Span | undefined =>
  (box as TextBoxShape | undefined)?.naturalAligns?.container;

const photoOf = (children: readonly Record<string, unknown>[]): Span | undefined =>
  spanOf(children.find(child => child['type'] === 'image'));

// Бокс по ширине целиком вне фото — подпись сбоку от фото.
function besidePhoto(box: Span | undefined, photo: Span | undefined): boolean {
  const [left, width, photoLeft, photoWidth] = [box?.left, box?.width, photo?.left, photo?.width];
  if (left === undefined || width === undefined || photoLeft === undefined || photoWidth === undefined) return false;
  return left >= photoLeft + photoWidth - 1e-6 || left + width <= photoLeft + 1e-6;
}

// Ширина подписи — фото, а бокс дизайнера шире фото (подпись рядом с узким фото) — по боксу; подпись сбоку от фото —
// по своему боксу: фото шире колонки текста, и строка по фото вылезла бы за колонку.
const captionWidthOf = (box: Record<string, unknown> | undefined, photo: Span | undefined): number | undefined =>
  photo?.width && (besidePhoto(spanOf(box), photo) ? spanOf(box)?.width : Math.max(photo.width, spanOf(box)?.width ?? 0));

// Точечная подпись ячейки пака на виньетке, движки её не ужимают (autoFit не рисуется): кегль дизайна, пока строка
// влезает в ширину подписи, иначе ужат по ней; без фото — кегль × образец / имя. Старт закреплён в maxFontSize,
// иначе каждая подстановка сжимала бы уже сжатое.
type SampleFit = {
  size: number;
  start: number;
  fit: (text: string) => number;
  handSet: boolean;
  legacy: boolean;
  sampled: number;
};

function sampleFitOf(child: Record<string, unknown>, previousText: string, photo?: Span): SampleFit | undefined {
  const sample = (child['templateParams'] as { text?: string } | undefined)?.text ?? '';
  const size = (child as TextBoxShape).naturalAligns?.fontSize;
  if (!sample.trim() || child['textMode'] !== 'free' || child['autoFit'] || !size) return undefined;

  const pinned = child['maxFontSize'] as number | undefined;
  const start = pinned ?? size;
  const width = captionWidthOf(child, photo);
  const bySample = (text: string): number => start * Math.min(1, longestLine(sample) / longestLine(text));
  const fit = (text: string): number => (width ? Math.min(start, width / Math.max(captionEms(text), 1e-6)) : bySample(text));
  // Кегль, не равный подгонке прежнего текста ни по ширине, ни по образцу, задан рукой (панель, ресайз ячейки).
  const autoFitted = (expected: number): boolean => Math.abs(size - expected) <= 0.01;
  const handSet = pinned !== undefined && !autoFitted(fit(previousText)) && !autoFitted(bySample(previousText));
  // Кегль от прежней подгонки по образцу (ужимала по длине, а не по ширине).
  const legacy = autoFitted(bySample(previousText)) && !autoFitted(fit(previousText));
  return { size, start, fit, handSet, legacy, sampled: bySample(previousText) };
}

/**
 * Кегль дизайна бокса подписи и задан ли он рукой — одна мерка на подстановку и на ужим строки переноса.
 * Бокс с образцом пака меряется подгонкой по образцу (`sampleFitOf`). Прочий бокс держит кегль дизайна в пине
 * ужима (`shrinkToCaption`): кегль, равный ужиму прежнего текста до ширины подписи, — авто, иной — рука.
 * `handSet` выключает перенос (как у пака), `hand` — ужим и возврат к дизайну.
 */
type CaptionFit = { start: number | undefined; handSet: boolean; hand: boolean; sampleFit: SampleFit | undefined };

function captionFitOf(child: Record<string, unknown>, previousText: string, photo?: Span): CaptionFit {
  const sampleFit = sampleFitOf(child, previousText, photo);
  if (sampleFit) return { start: sampleFit.start, handSet: sampleFit.handSet, hand: sampleFit.handSet, sampleFit };

  const size = (child as TextBoxShape).naturalAligns?.fontSize;
  // У autoFit maxFontSize — потолок движка, а не пин ужима.
  const pinned = child['autoFit'] ? undefined : (child['maxFontSize'] as number | undefined);
  if (pinned === undefined || !size) return { start: size, handSet: false, hand: false, sampleFit };

  const width = captionWidthOf(child, photo);
  const ems = captionEms(previousText);
  const shrunk = width && ems > 0 ? Math.min(pinned, width / ems) : pinned;
  // Рука решает перенос своим кеглем, ужатый нами бокс — кеглем дизайна. Кегль на пине — дизайн, а не рука:
  // «Заменить персону» пишет строку переноса на кегле дизайна, и ужать её до ширины подписи может только пак.
  const hand = Math.abs(size - shrunk) > 0.01 && Math.abs(size - pinned) > 0.01;
  return { start: hand ? size : pinned, handSet: false, hand, sampleFit };
}

// Бокс, ужатый прежней строкой переноса, к новой подписи возвращается к кеглю дизайна.
function withDesignSize(child: Record<string, unknown>, fit: CaptionFit): Record<string, unknown> {
  const size = (child as TextBoxShape).naturalAligns?.fontSize;
  if (fit.hand || fit.sampleFit?.legacy || !size || !fit.start || fit.start <= size) return child;
  return withFontSize(child, fit.start, size);
}

function withFontSize(child: Record<string, unknown>, fontSize: number, size: number): Record<string, unknown> {
  const text = child['text'] as Record<string, unknown>;
  const letterSpacing = text['letterSpacing'];
  return {
    ...child,
    naturalAligns: { ...(child['naturalAligns'] as Record<string, unknown>), fontSize },
    text: typeof letterSpacing === 'number' ? { ...text, letterSpacing: (letterSpacing * fontSize) / size } : text,
  };
}

/**
 * Подписи, переписанные мимо пака («Заменить персону», снятие персоны), сперва возвращаются к кеглю дизайна:
 * пак мерит ужатый кегль по тексту в боксе, и под чужим текстом счёл бы его рукой. Мерка та же, что у пака,
 * и только строки ФИО без образца — бокс с образцом пак подгоняет к новому тексту сам (`fitCaptionToSample`).
 */
export function withCaptionDesignSizes<T extends object>(children: readonly T[]): T[] {
  const { fits, boxes } = captionInputsOf(children as unknown as Record<string, unknown>[]);
  const slots = positionalSlots(boxes);
  let textIndex = 0;
  return children.map(child => {
    const box = child as unknown as Record<string, unknown>;
    if (box['type'] !== 'text') return child;
    const index = textIndex++;
    const nameLine = slots[index] === 0 || slots[index] === 1;
    return nameLine && !fits[index].sampleFit ? (withDesignSize(box, fits[index]) as T) : child;
  });
}

const NAME_CAPTION_TEXT_TYPES: ReadonlySet<string> = new Set([
  TextTypeEnum.PERSON_FULL_NAME,
  TextTypeEnum.PERSON_LAST_NAME,
  TextTypeEnum.PERSON_FIRST_NAME,
  TextTypeEnum.PERSON_GIVEN_PATRONYMIC,
  TextTypeEnum.PERSON_PATRONYMIC,
]);

/** Строки имени среди текстов группы: две первые позиционные и боксы, размеченные полем имени. */
function nameLineFlags(texts: readonly { textType?: string }[]): boolean[] {
  const slots = positionalSlots(texts);
  return texts.map((text, index) => slots[index] === 0 || slots[index] === 1 || NAME_CAPTION_TEXT_TYPES.has(text.textType ?? ''));
}

const textChildren = (children: readonly object[]): Record<string, unknown>[] =>
  (children as readonly Record<string, unknown>[]).filter(child => child['type'] === 'text');

/**
 * Кегль имени — общий для класса: строки имени ячейки встают на него как на кегль дизайна (пин ужима), а имя
 * шире подписи сразу ужато до неё той же подгонкой, что у подстановки. Так кегль не читается рукой, и следующая
 * подстановка переносит и ужимает длинное имя сама, а короткое держит на кегле класса.
 */
function withCaptionClassSize<T extends object>(children: readonly T[], fontSize: number): T[] {
  const boxes = children as unknown as Record<string, unknown>[];
  const photo = photoOf(boxes);
  const names = nameLineFlags(textChildren(boxes) as { textType?: string }[]);
  let textIndex = 0;
  return children.map(child => {
    const box = child as unknown as Record<string, unknown>;
    if (box['type'] !== 'text' || !names[textIndex++]) return child;
    const size = (box as TextBoxShape).naturalAligns?.fontSize;
    if (!size) return child;

    const pinned: Record<string, unknown> = { ...box, maxFontSize: fontSize };
    // autoFit кегль подбирает движок, пин для него — потолок.
    if (box['autoFit']) return withFontSize(pinned, fontSize, size) as T;
    const text = textOf(box);
    const atClassSize = { ...pinned, naturalAligns: { ...(pinned['naturalAligns'] as object), fontSize } };
    const sampleFit = sampleFitOf(atClassSize, text, photo);
    const width = captionWidthOf(box, photo);
    const ems = captionEms(text);
    const fitted = sampleFit ? sampleFit.fit(text) : width && ems > 0 ? Math.min(fontSize, width / ems) : fontSize;
    return withFontSize(pinned, fitted, size) as T;
  });
}

/** Типографика подписи: высота строки — кегль и ещё 20% интерлиньяжа. */
const CAPTION_LEADING = 1.2;

// Подписи ячейки, что живут правилами типографики: строки ФИО и предмет.
function captionFlags(children: readonly Record<string, unknown>[]): boolean[] {
  const texts = textChildren(children) as { textType?: string }[];
  const names = nameLineFlags(texts);
  const slots = positionalSlots(texts);
  let textIndex = 0;
  return children.map(child => {
    if (child['type'] !== 'text') return false;
    const index = textIndex++;
    return names[index] || child['textType'] === TextTypeEnum.PERSON_SUBJECT || slots[index] === 2;
  });
}

// Поле строки подписи прижато к тексту снизу: строк × кегль × интерлиньяж, пустое поле — в ноль. Ужатая подпись без
// пина ужима получает его на кегле до ужатия: следующая подстановка вернёт её к нему (`withCaptionsBeforeLeading`).
function withLeading(box: Record<string, unknown>, scale: number): Record<string, unknown> {
  const size = (box as TextBoxShape).naturalAligns?.fontSize;
  if (!size) return box;
  // Ужатый кегль — с шагом в тысячную пикселя вниз: поиск даёт его с шумом в последних знаках, и второй проход по той
  // же ячейке сдвигал бы модель на этот шум.
  const fontSize = scale === 1 ? size : Math.floor(size * scale * 1000) / 1000;
  const pin = scale !== 1 && box['maxFontSize'] === undefined ? { maxFontSize: size } : {};
  const scaled = scale === 1 ? box : withFontSize({ ...box, ...pin }, fontSize, size);
  const aligns = (scaled as TextBoxShape).naturalAligns;
  const text = textOf(scaled);
  const height = text.trim() === '' ? 0 : lineCount(text, textLayoutOf(scaled)) * fontSize * CAPTION_LEADING;
  return {
    ...scaled,
    text: { ...(scaled['text'] as Record<string, unknown>), lineHeight: CAPTION_LEADING },
    naturalAligns: { ...aligns, container: { ...aligns?.container, height } },
  };
}

const captionBottom = (children: readonly Record<string, unknown>[], flags: readonly boolean[]): number =>
  children.reduce((bottom, child, index) => {
    const container = (child as TextBoxShape).naturalAligns?.container;
    return flags[index] && container?.top !== undefined ? Math.max(bottom, container.top + (container.height ?? 0)) : bottom;
  }, Number.NEGATIVE_INFINITY);

/**
 * Подписи ячейки на кегле класса по правилам типографики, когда слова уже подставлены: поле каждой строки прижато к
 * тексту снизу, всё ниже выросшей или поджатой строки съезжает. Стопка ушла ниже своей границы (`captionLimitOf`) —
 * кегли подписей этой ячейки ужимаются вместе до наибольших, при которых стопка в ней. Подписи, что шаблон и так
 * поставил ниже границы, не судятся.
 */
function layCaptionLeading(children: Record<string, unknown>[], captionLimit: number | undefined): Record<string, unknown>[] {
  const flags = captionFlags(children);
  const at = (scale: number): Record<string, unknown>[] =>
    restackCaptions(
      children,
      children.map((child, index) => (flags[index] ? withLeading(child, scale) : child)),
    );
  const laid = at(1);
  const limit = (captionLimit ?? Number.POSITIVE_INFINITY) + 1e-6;
  if (captionBottom(laid, flags) <= limit || captionBottom(children, flags) > limit) return laid;

  let low = 0;
  let high = 1;
  for (let step = 0; step < 30; step++) {
    const middle = (low + high) / 2;
    if (captionBottom(at(middle), flags) <= limit) low = middle;
    else high = middle;
  }

  return at(low);
}

/**
 * Докуда растёт стопка подписей ячейки: низ ячейки шаблона и дальше белое поле под ней, пока подпись стоит к фото ряда
 * ниже не ближе, чем к своему, — снизу ей остаётся тот же зазор, что между её фото и первой строкой, иначе подпись
 * читается чужой. Ячейка шаблона кончается сразу под подписью дизайна, а поле между рядами — тоже место подписи.
 * Поля под ячейкой не знаем — граница сама ячейка.
 */
function captionLimitOf(
  children: Record<string, unknown>[],
  cellHeight: number | undefined,
  gapBelow: number | undefined,
): number | undefined {
  if (cellHeight === undefined || gapBelow === undefined) return cellHeight;
  const flags = captionFlags(children);
  const firstLine = children.reduce((top, child, index) => {
    const line = flags[index] ? boxTop(child) : undefined;
    return line === undefined ? top : Math.min(top, line);
  }, Number.POSITIVE_INFINITY);
  const photo = (children.find(child => child['type'] === 'image') as TextBoxShape | undefined)?.naturalAligns?.container;
  const photoBottom = photo?.top !== undefined && photo.height !== undefined ? photo.top + photo.height : undefined;
  const air = photoBottom !== undefined && Number.isFinite(firstLine) ? Math.max(0, firstLine - photoBottom) : 0;
  return Math.max(cellHeight, cellHeight + gapBelow - air);
}

type PageBox = { top: number; left: number; width: number; height: number };

const pageBoxOf = (drawable: Record<string, unknown>): PageBox | undefined => {
  const box = (drawable as { naturalAligns?: { container?: Partial<PageBox> } }).naturalAligns?.container;
  const sides = [box?.top, box?.left, box?.width, box?.height];
  return sides.every(side => typeof side === 'number' && Number.isFinite(side)) ? (box as PageBox) : undefined;
};

// Невидимый бокс и пустой текст на странице ничего не рисуют — подписи над ними не упираются.
const drawsOnPage = (drawable: Record<string, unknown>): boolean =>
  drawable['visible'] !== false && (drawable['type'] !== 'text' || textOf(drawable).trim() !== '');

const NO_GAPS: ReadonlyMap<object, number> = new Map();

/**
 * Белое поле под каждой ячейкой ростера до того, что стоит ниже неё на той же странице и перекрывает её по ширине, —
 * промежуток между рядами. У ячейки последнего ряда соседа снизу нет: она берёт самый узкий промежуток разворота,
 * ритм рядов тот же. Ни у одной ячейки соседа снизу нет — поля не знаем.
 */
function gapsBelowCells(
  collages: readonly Record<string, unknown>[],
  isRosterCell: RosterCellCheck,
): ReadonlyMap<object, number> {
  const gaps = new Map<object, number>();
  const lastRow: object[] = [];
  for (const collage of collages) {
    const drawables = (collage['drawableObjects'] as Record<string, unknown>[] | undefined) ?? [];
    for (const cell of drawables) {
      const box = cell['type'] === 'group' && isRosterCell(cell) ? pageBoxOf(cell) : undefined;
      if (!box) continue;
      const bottom = box.top + box.height;
      const gap = drawables.reduce((nearest, other) => {
        const below = other !== cell && drawsOnPage(other) ? pageBoxOf(other) : undefined;
        const overlaps = below && below.left < box.left + box.width && below.left + below.width > box.left;
        return overlaps && below.top >= bottom - 1e-6 ? Math.min(nearest, below.top - bottom) : nearest;
      }, Number.POSITIVE_INFINITY);
      if (Number.isFinite(gap)) gaps.set(cell, gap);
      else lastRow.push(cell);
    }
  }

  if (gaps.size === 0) return NO_GAPS;

  const rhythm = Math.min(...gaps.values());
  for (const cell of lastRow) gaps.set(cell, rhythm);
  return gaps;
}

/**
 * Предмет на кегле класса — по ширине подписи, как ФИО: не влезает строкой — переносится по словам тем же кеглем, слово
 * шире подписи ужимает кегль предмета до себя. Поле строки под него кладёт проход интерлиньяжа.
 */
function withSubjectToWidth(children: Record<string, unknown>[], photo: Span | undefined): Record<string, unknown>[] {
  const slots = positionalSlots(textChildren(children) as { textType?: string }[]);
  let textIndex = 0;
  return children.map(child => {
    if (child['type'] !== 'text') return child;
    const index = textIndex++;
    const size = (child as TextBoxShape).naturalAligns?.fontSize;
    const width = captionWidthOf(child, photo);
    const words = textOf(child).split(/\s+/).filter(Boolean);
    const subject = child['textType'] === TextTypeEnum.PERSON_SUBJECT || slots[index] === 2;
    if (!subject || !size || !width || words.length === 0) return child;

    const widest = Math.max(...words.map(word => captionEms(word)));
    const fontSize = widest > 0 ? Math.min(size, width / widest) : size;
    const lines = linesWithin(words, width / fontSize);
    const sized = fontSize < size - 0.01 ? withFontSize(child, fontSize, size) : child;
    return { ...sized, text: { ...(sized['text'] as Record<string, unknown>), text: lines } };
  });
}

// Слова строками не шире `ems` кеглей: жадный перенос по пробелам.
function linesWithin(words: readonly string[], ems: number): string {
  const lines: string[] = [];
  for (const word of words) {
    const last = lines[lines.length - 1];
    if (last !== undefined && captionEms(`${last} ${word}`) <= ems + 1e-6) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }

  return lines.join('\n');
}

/**
 * Имя ячейки на кегле класса — одним кеглем: слово шире фото ужимает все строки этого имени, а не только свою, —
 * авторазмер у имени, а не у слова. Пустая строка имени кегля не держит.
 */
function withOneNameSize(children: Record<string, unknown>[]): Record<string, unknown>[] {
  const names = nameLineFlags(textChildren(children) as { textType?: string }[]);
  const sizeOf = (box: Record<string, unknown>): number | undefined => (box as TextBoxShape).naturalAligns?.fontSize;
  const sizes = textChildren(children).flatMap((text, index) => {
    const size = sizeOf(text);
    return names[index] && size && textOf(text).trim() !== '' ? [size] : [];
  });
  if (sizes.length < 2) return children;

  const smallest = Math.min(...sizes);
  let textIndex = 0;
  return children.map(child => {
    if (child['type'] !== 'text') return child;
    const index = textIndex++;
    const size = sizeOf(child);
    if (!names[index] || !size || textOf(child).trim() === '' || size - smallest <= 0.01) return child;
    return withFontSize(child, smallest, size);
  });
}

/**
 * Подписи выросли с кеглем класса — всё, что стоит под выросшей строкой в той же ячейке, сдвигается на её прирост:
 * вторая строка ФИО, предмет и цитата не наезжают на первую. Фото и тексты выше строки остаются на местах.
 */
function restackCaptions(before: Record<string, unknown>[], after: Record<string, unknown>[]): Record<string, unknown>[] {
  const grown = before.flatMap((box, index) => {
    const was = containerOf(box) as (CaptionContainer & { top?: number; height?: number }) | undefined;
    const now = containerOf(after[index]) as (CaptionContainer & { height?: number }) | undefined;
    if (box['type'] !== 'text' || was?.top === undefined || was.height === undefined || now?.height === undefined) return [];
    const growth = now.height - was.height;
    return Math.abs(growth) > 1e-6 ? [{ index, bottom: was.top + was.height, growth }] : [];
  });
  if (grown.length === 0) return after;

  return after.map((box, index) => {
    const top = boxTop(before[index]);
    if (box['type'] !== 'text' || top === undefined) return box;
    const shift = grown.reduce((sum, row) => (row.index !== index && top >= row.bottom - 1e-6 ? sum + row.growth : sum), 0);
    const current = boxTop(box);
    return shift !== 0 && current !== undefined ? withTop(box, current + shift) : box;
  });
}

const sameSize = (a: number | undefined, b: number): boolean => a !== undefined && Math.abs(a - b) <= 0.01;

/** Кегли класса на детях ячейки: строки имени — кегль ФИО роли, предмет педагога — кегль предмета. */
function withClassCaptionSizes(
  children: Record<string, unknown>[],
  sizes: VignetteCaptionSizes,
  teacher: boolean,
): Record<string, unknown>[] {
  const subject = teacher ? sizes.subject : undefined;
  const name = teacher ? sizes.teacher : sizes.student;
  if (!name && !subject) return children;
  const wide = withCaptionsBeforeLeading(widenCaptionsToPhoto(children), !!name);
  const named = name && !sameSize(captionClassSizeOf(wide), name) ? withCaptionClassSize(wide, name) : wide;
  return subject ? withSubjectSize(named, subject) : named;
}

/**
 * Подписи ячейки — к кеглям до прохода интерлиньяжа прошлой подстановки: он ужимал их вместе, и подстановка раскладывает
 * ячейку заново от тех же кеглей, иначе каждая следующая ужимала бы уже ужатое. Ужатая подпись держит кегль до ужатия в
 * пине (`withLeading`); подпись с образцом пака встаёт на подгонку образца от пина. Строки имени с кеглем класса к нему
 * возвращает `withCaptionClassSize`. На ячейке с кеглем класса подписи раскладывает подстановка, кегль руки не держится.
 */
function withCaptionsBeforeLeading(children: Record<string, unknown>[], nameClassSized: boolean): Record<string, unknown>[] {
  const flags = captionFlags(children);
  const names = nameLineFlags(textChildren(children) as { textType?: string }[]);
  const photo = photoOf(children);
  let textIndex = 0;
  return children.map((child, index) => {
    if (child['type'] !== 'text') return child;
    const name = names[textIndex++];
    const size = (child as TextBoxShape).naturalAligns?.fontSize;
    const pin = child['maxFontSize'] as number | undefined;
    if (!flags[index] || (name && nameClassSized) || !size || pin === undefined || size >= pin - 0.01) {
      return child;
    }

    const atPin = withFontSize(child, pin, size);
    const sampleFit = sampleFitOf(atPin, textOf(child), photo);
    return sampleFit ? withFontSize(child, sampleFit.fit(textOf(child)), size) : atPin;
  });
}

type CaptionContainer = { left?: number; width?: number };
const containerOf = (box: Record<string, unknown> | undefined): CaptionContainer | undefined =>
  (box as { naturalAligns?: { container?: CaptionContainer } } | undefined)?.naturalAligns?.container;

/**
 * Подпись на кегле класса — шириной с фото: поле уже фото переносит ФИО и предмет раньше, чем они упрутся в рамку.
 * Тянутся только ФИО и предмет под фото (середина поля над фото); подпись сбоку от фото и прочие тексты — как были.
 */
function widenCaptionsToPhoto(children: Record<string, unknown>[]): Record<string, unknown>[] {
  const photo = containerOf(children.find(child => child['type'] === 'image'));
  const photoLeft = photo?.left;
  const photoWidth = photo?.width;
  if (photoLeft === undefined || !photoWidth) return children;
  const texts = textChildren(children) as { textType?: string }[];
  const names = nameLineFlags(texts);
  const slots = positionalSlots(texts);
  let textIndex = 0;
  return children.map(child => {
    if (child['type'] !== 'text') return child;
    const index = textIndex++;
    const caption = names[index] || child['textType'] === TextTypeEnum.PERSON_SUBJECT || slots[index] === 2;
    const box = containerOf(child);
    if (!caption || box?.left === undefined || !box.width || box.width >= photoWidth - 1e-6) return child;
    const center = box.left + box.width / 2;
    if (center < photoLeft || center > photoLeft + photoWidth) return child;
    const aligns = child['naturalAligns'] as Record<string, unknown>;
    return { ...child, naturalAligns: { ...aligns, container: { ...box, left: photoLeft, width: photoWidth } } };
  });
}

// Предмет — бокс с типом предмета или третий позиционный слот педагога; его пин ужима встаёт на кегль класса.
function withSubjectSize(children: Record<string, unknown>[], fontSize: number): Record<string, unknown>[] {
  const texts = textChildren(children) as { textType?: string }[];
  const slots = positionalSlots(texts);
  let textIndex = 0;
  return children.map(child => {
    if (child['type'] !== 'text') return child;
    const index = textIndex++;
    const subject = child['textType'] === TextTypeEnum.PERSON_SUBJECT || slots[index] === 2;
    const size = (child as TextBoxShape).naturalAligns?.fontSize;
    const design = (child['maxFontSize'] as number | undefined) ?? size;
    if (!subject || !size || sameSize(design, fontSize)) return child;
    return withFontSize({ ...child, maxFontSize: fontSize }, fontSize, size);
  });
}

/** Кегль класса у строк имени группы: рука — свой кегль, авто — кегль дизайна; разные или нет строк — `undefined`. */
function captionClassSizeOf(children: readonly object[]): number | undefined {
  const { fits } = captionInputsOf(children as readonly Record<string, unknown>[]);
  const texts = textChildren(children);
  const names = nameLineFlags(texts as { textType?: string }[]);
  const sizes = texts.flatMap((text, index) => {
    if (!names[index]) return [];
    const size = (text as TextBoxShape).naturalAligns?.fontSize;
    // У autoFit старт подгонки — его кегль: потолок `maxFontSize` равен кеглю класса и у ужатой строки.
    const design = fits[index].hand ? size : fits[index].start;
    return design ? [design] : [];
  });
  return sizes.length > 0 && sizes.every(size => Math.abs(size - sizes[0]) <= 0.01) ? sizes[0] : undefined;
}

export type CaptionSizeKey = keyof VignetteCaptionSizes;

// Чей кегль класса несёт текст ячейки: строка имени — роли ячейки, предмет — педагога; прочее — ничей.
function captionSizeKeyIn(group: DrawableGroupData, boxId: string): CaptionSizeKey | undefined {
  if (!groupCarriesPerson(group)) return undefined;
  const texts = textChildren(group.drawableObjects ?? []);
  const index = texts.findIndex(text => text['id'] === boxId);
  if (index < 0) return undefined;
  const typed = texts as { textType?: string }[];
  const teacher = isTeacherGroup(group);
  if (nameLineFlags(typed)[index]) return teacher ? 'teacher' : 'student';
  const subject = typed[index].textType === TextTypeEnum.PERSON_SUBJECT || (teacher && positionalSlots(typed)[index] === 2);
  return subject ? 'subject' : undefined;
}

/**
 * Чей кегль класса правит бокс разворота: ФИО учеников, ФИО педагогов или предмет; не подпись ячейки — `undefined`.
 * Кегль класса кладётся только на ячейки ростера виньетки — карточка педагога на обложке или текст владельца на личном
 * развороте держат свой кегль, как любой текст. Карточку серии цитат кегль класса достаёт только пересборкой серии
 * (подстановка её подпись не перекладывает): без пересборки (`quoteCards: false`) её подпись — ничья.
 */
export function captionSizeKeyOf(
  spread: Pick<SpreadData, 'collages' | 'spread_type'>,
  boxId: string,
  options: { quoteCards?: boolean } = {},
): CaptionSizeKey | undefined {
  if (!surfaceIsRoster(spread as unknown as Record<string, unknown>)) return undefined;
  const isRosterCell = rosterCellsOf(spread as unknown as RosterSurface, true);
  let key: CaptionSizeKey | undefined;
  for (const collage of spread.collages ?? []) {
    forEachDrawableDeep(collage.drawableObjects ?? [], drawable => {
      if (key || !isDrawableGroup(drawable) || !isRosterCell(drawable as unknown as Record<string, unknown>)) return;
      const children = (drawable.drawableObjects ?? []) as unknown as Record<string, unknown>[];
      if (options.quoteCards === false && isQuoteCard(children)) return;
      key = captionSizeKeyIn(drawable, boxId);
    });
  }

  return key;
}

// Кегль дизайна предмета ячейки: пин ужима, без него — кегль; предмета нет — `undefined`.
function subjectSizeOf(group: DrawableGroupData): number | undefined {
  const texts = textChildren(group.drawableObjects ?? []);
  const subject = texts.find(text => captionSizeKeyIn(group, text['id'] as string) === 'subject');
  if (!subject) return undefined;

  return (subject['maxFontSize'] as number | undefined) ?? (subject as TextBoxShape).naturalAligns?.fontSize;
}

/**
 * Ячейки, куда заливка кладёт кегль класса, одной поверхностью: ростер разворота виньетки (тип, а не слот), без рамки
 * владельца. По ней судят информер и поле «Размер» — иначе они меряли бы ячейки, которых кегль класса не достаёт.
 */
export function rosterCaptionSurface(
  spreads: readonly Pick<SpreadData, 'collages' | 'spread_type'>[],
): Pick<SpreadData, 'collages'> {
  const collages = spreads.flatMap(spread => {
    if (!surfaceIsRoster(spread as unknown as Record<string, unknown>)) return [];
    const isRosterCell = rosterCellsOf(spread as unknown as RosterSurface, true);
    const cells: DrawableGroupData[] = [];
    for (const collage of spread.collages ?? []) {
      forEachDrawableDeep(collage.drawableObjects ?? [], drawable => {
        if (isDrawableGroup(drawable) && isRosterCell(drawable as unknown as Record<string, unknown>)) cells.push(drawable);
      });
    }

    return [{ drawableObjects: cells }];
  });
  return { collages } as unknown as Pick<SpreadData, 'collages'>;
}

/**
 * Кегли подписей, что стоят на развороте сейчас, — с них поле «Размер» начинает, пока кегль класса не задан.
 * По ключу — кегль большинства ячеек (разошлись поровну — крупнейший): кегль руки в одной ячейке его не сдвигает.
 */
export function spreadCaptionSizes(spread: Pick<SpreadData, 'collages'>): VignetteCaptionSizes {
  const counts = new Map<CaptionSizeKey, Map<number, number>>();
  const note = (key: CaptionSizeKey, size: number | undefined): void => {
    if (size === undefined) return;
    const bySize = counts.get(key) ?? new Map<number, number>();
    const rounded = Math.round(size * 100) / 100;
    bySize.set(rounded, (bySize.get(rounded) ?? 0) + 1);
    counts.set(key, bySize);
  };

  for (const collage of spread.collages ?? []) {
    forEachDrawableDeep(collage.drawableObjects ?? [], drawable => {
      if (!isDrawableGroup(drawable) || !groupCarriesPerson(drawable)) return;
      const teacher = isTeacherGroup(drawable);
      const children = drawable.drawableObjects ?? [];
      const texts = textChildren(children);
      const names = nameLineFlags(texts as { textType?: string }[]);
      const { fits } = captionInputsOf(children as unknown as Record<string, unknown>[]);
      texts.forEach((text, index) => {
        if (!names[index]) return;
        const size = (text as TextBoxShape).naturalAligns?.fontSize;
        note(teacher ? 'teacher' : 'student', fits[index].hand ? size : fits[index].start);
      });
      if (teacher) note('subject', subjectSizeOf(drawable));
    });
  }

  const mode = (bySize: Map<number, number>): number =>
    [...bySize].reduce((best, entry) => (entry[1] > best[1] || (entry[1] === best[1] && entry[0] > best[0]) ? entry : best))[0];
  return Object.fromEntries([...counts].map(([key, bySize]) => [key, mode(bySize)])) as VignetteCaptionSizes;
}

/** Подпись ячейки мельче кегля класса: чья, по какому кеглю и до какого кегля ужата. */
export interface CaptionBelowClassSize {
  personId: string;
  key: CaptionSizeKey;
  size: number;
}

/**
 * Подписи после подстановки мельче кегля класса — слово шире фото и отдельной строкой или строки ниже ячейки. По персоне
 * и кеглю одна запись с самым мелким кеглем, порядок — порядок ячеек: кегль класса, при котором влезут все, —
 * самый мелкий из них. Карточки цитат без пересборки серии (`quoteCards: false`) кегль класса не достаёт — их не судит.
 */
export function captionsBelowClassSize(
  spread: Pick<SpreadData, 'collages'>,
  sizes: VignetteCaptionSizes,
  options: { quoteCards?: boolean } = {},
): CaptionBelowClassSize[] {
  const below: CaptionBelowClassSize[] = [];
  for (const collage of spread.collages ?? []) {
    forEachDrawableDeep(collage.drawableObjects ?? [], drawable => {
      if (!isDrawableGroup(drawable) || !groupCarriesPerson(drawable)) return;
      const personId = (drawable.params as { personId?: string | null } | undefined)?.personId;
      if (!personId) return;
      const children = (drawable.drawableObjects ?? []) as unknown as Record<string, unknown>[];
      if (options.quoteCards === false && isQuoteCard(children)) return;
      for (const text of textChildren(drawable.drawableObjects ?? [])) {
        const key = captionSizeKeyIn(drawable, text['id'] as string);
        const classSize = key && sizes[key];
        const actual = (text as TextBoxShape).naturalAligns?.fontSize;
        if (!key || !classSize || !actual || textOf(text).trim() === '' || actual >= classSize - 0.01) continue;
        const known = below.find(entry => entry.personId === personId && entry.key === key);
        if (!known) below.push({ personId, key, size: actual });
        else known.size = Math.min(known.size, actual);
      }
    });
  }

  return below;
}

// Ужим пишет кегль дизайна в maxFontSize, как подгонка по образцу: следующая подстановка решает от него.
function shrinkToCaption(child: Record<string, unknown>, photo: Span | undefined): Record<string, unknown> {
  const size = (child as TextBoxShape).naturalAligns?.fontSize;
  const width = captionWidthOf(child, photo);
  const ems = captionEms(textOf(child));
  if (!size || !width || ems <= 0 || ems * size <= width + 1e-6) return child;

  const pin = child['maxFontSize'] === undefined && !child['autoFit'] ? { maxFontSize: size } : {};
  return { ...withFontSize(child, width / ems, size), ...pin };
}

function fitCaptionToSample(child: Record<string, unknown>, previousText: string, photo?: Span): Record<string, unknown> {
  const sampleFit = sampleFitOf(child, previousText, photo);
  return !sampleFit || sampleFit.handSet ? child : withSampleFit(child, sampleFit);
}

function withSampleFit(child: Record<string, unknown>, { size, start, fit }: SampleFit): Record<string, unknown> {
  const aligns = child['naturalAligns'] as Record<string, unknown>;
  const text = child['text'] as Record<string, unknown>;
  const fontSize = fit(String(text['text'] ?? ''));
  const letterSpacing = text['letterSpacing'];
  return {
    ...child,
    maxFontSize: start,
    naturalAligns: { ...aligns, fontSize },
    text: typeof letterSpacing === 'number' ? { ...text, letterSpacing: (letterSpacing * fontSize) / size } : text,
  };
}

// Ресайз с разными долями по осям уводит ширину от кегля, и подгонку после него не узнать: классифицируем до него.
export function refitPackCaptionsAfterRescale<T extends object>(before: readonly T[], after: readonly T[]): T[] {
  const boxes = (children: readonly T[]): Record<string, unknown>[] => children as unknown as Record<string, unknown>[];
  const [beforePhoto, afterPhoto] = [photoOf(boxes(before)), photoOf(boxes(after))];
  return after.map((child, index) => {
    const box = boxes(after)[index];
    const previous = boxes(before)[index];
    if (box['type'] !== 'text' || previous?.['type'] !== 'text') return child;
    const auto = sampleFitOf(previous, textOf(previous), beforePhoto);
    if (!auto) return refitShrunkCaption(previous, box, beforePhoto, afterPhoto) as T;
    const sampleFit = !auto.handSet && !auto.legacy ? sampleFitOf(box, textOf(box), afterPhoto) : undefined;
    const refits = !!sampleFit && Math.abs(sampleFit.fit(textOf(box)) - sampleFit.size) > 0.01;
    return refits ? (withSampleFit(box, sampleFit) as T) : child;
  });
}

// Строка переноса без образца, ужатая паком до ширины подписи, после ресайза ужимается заново от пина: фото
// растёт по своей оси, кегль — по меньшей, и прежний ужим пак иначе счёл бы рукой.
function refitShrunkCaption(
  previous: Record<string, unknown>,
  box: Record<string, unknown>,
  beforePhoto: Span | undefined,
  afterPhoto: Span | undefined,
): Record<string, unknown> {
  const fit = captionFitOf(previous, textOf(previous), beforePhoto);
  const shrunk = (previous as TextBoxShape).naturalAligns?.fontSize;
  const pin = box['maxFontSize'] as number | undefined;
  const size = (box as TextBoxShape).naturalAligns?.fontSize;
  if (fit.hand || !fit.start || !shrunk || fit.start <= shrunk || !pin || !size) return box;
  return shrinkToCaption(withFontSize(box, pin, size), afterPhoto);
}

const sampleOf = (child: Record<string, unknown>): string =>
  (child['templateParams'] as { text?: string } | undefined)?.text?.trim() ?? '';

// Подпись роли педагога: размечена типом или стоит в слоте с образцом «Предмет».
const isRoleCaption = (child: Record<string, unknown>): boolean =>
  child['textType'] === TextTypeEnum.PERSON_SUBJECT || sampleOf(child) === textPlaceholderFor(TextTypeEnum.PERSON_SUBJECT);

// Подпись роли не ужимается, а переносится: слово за словом в строки не длиннее образца дизайнера, слово длиннее
// образца занимает строку целиком. Кегль остаётся дизайнерским: ужатый ранее под образец возвращается к потолку.
function wrapCaptionToSample(child: Record<string, unknown>, previousText: string): Record<string, unknown> {
  const sample = sampleOf(child);
  const aligns = child['naturalAligns'] as { fontSize?: number } | undefined;
  const size = aligns?.fontSize;
  if (!sample || child['textMode'] !== 'free' || child['autoFit'] || !size) return child;

  const text = child['text'] as Record<string, unknown>;
  const budget = longestLine(sample);
  const lines: string[] = [];
  for (const word of String(text['text'] ?? '')
    .split(/\s+/)
    .filter(Boolean)) {
    const last = lines[lines.length - 1];
    if (last !== undefined && last.length + 1 + word.length <= budget) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }

  const pinned = child['maxFontSize'] as number | undefined;
  const shrunk =
    pinned !== undefined &&
    size < pinned &&
    Math.abs(size - pinned * Math.min(1, longestLine(sample) / longestLine(previousText))) <= 0.01;
  const fontSize = shrunk ? pinned : size;
  const letterSpacing = text['letterSpacing'];
  return {
    ...child,
    naturalAligns: { ...aligns, fontSize },
    text: {
      ...text,
      text: lines.join('\n'),
      ...(shrunk && typeof letterSpacing === 'number' ? { letterSpacing: (letterSpacing * fontSize) / size } : {}),
    },
  };
}

// Бокс подписи для решения о переносе: кегль дизайна, ширина бокса, ручной ли кегль и стоит ли бокс сбоку от фото.
export type PersonCaptionBox = {
  textType?: string;
  fioLayout?: FioLayout;
  fontSize?: number;
  width?: number;
  handSet?: boolean;
  beside?: boolean;
};

// Кегль дизайна закреплён в maxFontSize, если подгонка его уже ужала; рука раскладку не меняет.
function captionInputsOf(children: readonly Record<string, unknown>[]): {
  photo: Span | undefined;
  fits: CaptionFit[];
  boxes: PersonCaptionBox[];
} {
  const photo = photoOf(children);
  const texts = children.filter(child => child['type'] === 'text');
  const fits = texts.map(child => captionFitOf(child, textOf(child), photo));
  const boxes = texts.map((child, index) => ({
    textType: child['textType'] as string | undefined,
    fioLayout: child['fioLayout'] as FioLayout | undefined,
    fontSize: fits[index].start,
    width: spanOf(child)?.width,
    handSet: fits[index].handSet,
    beside: besidePhoto(spanOf(child), photo),
  }));
  return { photo, fits, boxes };
}

/**
 * Входы решения о переносе по детям группы персоны — те же, что берёт сам пак: кегль дизайна из пина и рука.
 * Бокс без геометрии даёт `undefined`, и перенос не решается: подстановка — голая позиционная.
 */
export function personCaptionBoxes(children: readonly object[]): { boxes: PersonCaptionBox[]; photoWidth: number | undefined } {
  const { boxes, photo } = captionInputsOf(children as readonly Record<string, unknown>[]);
  return { boxes, photoWidth: photo?.width };
}

// ФИО одной строкой шире подписи (фото или бокс шире него) — фамилия в первом боксе, имя во втором; пак и генерация.
export function resolvePersonCaptionTexts(
  boxes: readonly PersonCaptionBox[],
  person: Person,
  nameFormat: NameFormat | CaptionRules,
  photoWidth: number | undefined,
  options?: PersonTextOptions,
): (string | undefined)[] {
  return planPersonCaptions(boxes, person, nameFormat, photoWidth, options).resolved;
}

function planPersonCaptions(
  boxes: readonly PersonCaptionBox[],
  person: Person,
  nameFormat: NameFormat | CaptionRules,
  photoWidth: number | undefined,
  options?: PersonTextOptions,
): { slots: (number | undefined)[]; oneLine: boolean; split: string[] | undefined; resolved: (string | undefined)[] } {
  const rules = captionRules(nameFormat);
  const teacher = isTeacher(person);
  const slots = positionalSlots(boxes);
  const first = boxes[slots.indexOf(0)];
  const formatOneLine = fillsOneBox(teacher, rules, slotCountOf(slots));
  const joined = resolvePersonTextsPositional(boxes, person, nameFormat, options);
  const line = joined[slots.indexOf(0)];
  const size = first?.fontSize;
  const width = photoWidth && (first?.beside ? first.width : Math.max(photoWidth, first?.width ?? 0));
  // Формат без второй части (только имя) остаётся одной строкой.
  const overflow =
    formatOneLine &&
    (teacher || wrapsWhenTooWide(personNameFormat(false, rules))) &&
    slots.includes(1) &&
    !first?.handSet &&
    !!line &&
    !!size &&
    !!width &&
    captionEms(line) * size > width + 1e-6;
  const split = overflow
    ? personNameSlots(person, rules, personNameFormat(teacher, rules), options?.strictBlanks === true)
    : undefined;
  const oneLine = formatOneLine && !(split?.[0] && split?.[1]);
  const resolved =
    oneLine || !split ? joined : joined.map((value, index) => (slots[index] === undefined ? value : split[slots[index]]));
  return { slots, oneLine, split, resolved };
}

/**
 * Что положить в каждый текст группы персоны; `undefined` — не трогать.
 * Персональный тип резолвится по смыслу, чужой не заполняется никогда, остальное — по позиции.
 */
export function resolvePersonTextsPositional(
  texts: readonly { textType?: string; fioLayout?: FioLayout }[],
  person: Person,
  nameFormat: NameFormat | CaptionRules,
  options?: PersonTextOptions,
): (string | undefined)[] {
  const slots = positionalSlots(texts);
  const positional = getPersonTexts(person, captionRules(nameFormat), slotCountOf(slots), options?.strictBlanks === true);
  const hasPersonMarkup = texts.some(text => isPersonMarkupField(text.textType));

  return texts.map((text, index) => {
    const slot = slots[index];
    if (slot !== undefined) return positional[slot];
    // Один закон на заполнение и на освобождение: `personOwnsGroupText` отвечает обоим одинаково.
    if (!personOwnsGroupText(text.textType, hasPersonMarkup)) return undefined;

    return resolveTextByType(person, text.textType, nameFormat, text.fioLayout, options);
  });
}

/**
 * ОДИН обход слотов персонной группы — на нём стоят и подстановка, и серая подсказка редактора.
 * Даёт по каждому тексту номер его позиционного слота (`getPersonTexts`) или `undefined`: бокс либо
 * размечен типом и резолвится по смыслу, либо принадлежит альбому и персону не касается.
 * Второго счётчика быть не должно: разойдись он с подстановкой — подсказка назовёт не то поле.
 */
function positionalSlots(texts: readonly { textType?: string }[]): (number | undefined)[] {
  // `freeText` парсер PSD ставит каждому тексту сам, а новый бокс редактора приходит вообще без типа —
  // поэтому «дизайнер разметил группу» означает ровно одно: в ней есть текст ПОЗИЦИОННОГО персонального
  // типа. Цитата к ним не относится: одна рамка цитаты в неразмеченной ячейке подстановку не гасит.
  const hasPersonMarkup = texts.some(text => isPersonMarkupField(text.textType));
  // Пропущенный бокс не занимает слот имени, иначе фамилия не попадёт никуда.
  let slot = 0;

  return texts.map(text =>
    personOwnsGroupText(text.textType, hasPersonMarkup) && !isPersonTextField(text.textType) ? slot++ : undefined,
  );
}

/** Тексты группы в том же порядке, в каком их считает подстановка: прямые дети, только `text`. */
function groupTexts(group: DrawableGroupData): DrawableTextData[] {
  return (group.drawableObjects ?? []).filter(isDrawableText);
}

/** Персонная ли группа — ровно мерка {@link resolveGroupPerson}: роль владельца либо ссылка на персону. */
function groupCarriesPerson(group: DrawableGroupData): boolean {
  const params = group.params as { personId?: string | null; peopleType?: string } | undefined;
  return isOwnerGroupPeopleType(params?.peopleType) || !!params?.personId;
}

function isTeacherGroup(group: DrawableGroupData): boolean {
  const peopleType = (group.params as { peopleType?: string } | undefined)?.peopleType;
  return peopleType === GroupPeopleType.TEACHER || peopleType === GroupPeopleType.CLASS_TEACHER;
}

/**
 * Слова позиционных слотов — зеркало {@link getPersonTexts} в словаре подсказок: что подстановка
 * положит в слот, то он и обещает пустым. Формат имени переставляет слова ровно так же, как значения;
 * у слота инициала слово остаётся «Имя» — обещать «И.» нечего, набирается всё равно имя.
 */
function positionalSlotWords(rules: CaptionRules, teacher: boolean, slotCount: number): string[] {
  const nameFormat = personNameFormat(teacher, rules);
  const words = slotWords(nameFormat, teacher);
  return fillsOneBox(teacher, rules, slotCount) ? fitSlotsToFormat(words, nameFormat, teacher) : words;
}

function slotWords(nameFormat: NameFormat, teacher: boolean): string[] {
  return slotTypes(nameFormat, teacher).map(textType => textPlaceholderFor(textType));
}

/** Что печатает каждый позиционный слот, — роль, слово которой обещает {@link slotWords}. */
function slotTypes(nameFormat: NameFormat, teacher: boolean): TextTypeEnum[] {
  const surname = TextTypeEnum.PERSON_LAST_NAME;
  const name = TextTypeEnum.PERSON_FIRST_NAME;

  if (teacher) {
    const namePatronymic = TextTypeEnum.PERSON_GIVEN_PATRONYMIC;
    const subject = TextTypeEnum.PERSON_SUBJECT;
    switch (nameFormat) {
      case NameFormat.FIRST_PATRONYMIC_SURNAME:
      case NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME:
      case NameFormat.FIRST_BREAK_PATRONYMIC_BREAK_SURNAME:
        return [namePatronymic, surname, subject];
      case NameFormat.INITIALS_SURNAME:
        return [name, surname, subject];
      case NameFormat.SURNAME_INITIALS:
        return [surname, name, subject];
      default:
        return [surname, namePatronymic, subject];
    }
  }

  const givenFirst: readonly NameFormat[] = [
    NameFormat.FIRST_PATRONYMIC_SURNAME,
    NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME,
    NameFormat.FIRST_ONLY,
  ];
  return givenFirst.includes(nameFormat) ? [name, surname] : [surname, name];
}

/**
 * Бокс, который подстановка заполнит ПО ПОЗИЦИИ: своего типа он не носит, и что в нём напечатается,
 * знает только порядок внутри группы. Спрашивают холст, метки превью и гейт готовности — все трое.
 */
export function isPositionalNameSlot(box: DrawableData, group: DrawableGroupData): boolean {
  if (!isDrawableText(box) || !groupCarriesPerson(group)) return false;

  const texts = groupTexts(group);
  const index = texts.findIndex(text => text.id === box.id);
  return index >= 0 && positionalSlots(texts)[index] !== undefined;
}

/**
 * Безымянные боксы, которые подстановка РЕАЛЬНО заполняет: слоты обхода {@link positionalSlots} с номером
 * меньше `capacity` — числа слов {@link getPersonTexts} (ученик 2, педагог 3). Бокс за ними принадлежит альбому.
 * Группу владельца заполняет владелец альбома, поэтому её мерку задаёт `ownerIsTeacher` (`personHasPatronymic`
 * владельца); не передан — мерка по `peopleType` группы.
 */
export function positionalNameSlots(
  group: DrawableGroupData,
  ownerIsTeacher?: boolean,
): { ids: ReadonlySet<string>; capacity: number } {
  const peopleType = (group.params as { peopleType?: string } | undefined)?.peopleType;
  const teacher = ownerIsTeacher !== undefined && isOwnerGroupPeopleType(peopleType) ? ownerIsTeacher : isTeacherGroup(group);
  const capacity = slotWords(NameFormat.SURNAME_FIRST_PATRONYMIC, teacher).length;
  const texts = groupTexts(group);
  const slots = positionalSlots(texts);

  return { ids: new Set(texts.filter((_, i) => (slots[i] ?? capacity) < capacity).map(text => text.id)), capacity };
}

const PLACEHOLDER_PERSON = {
  name: textPlaceholderFor(TextTypeEnum.PERSON_FIRST_NAME),
  surname: textPlaceholderFor(TextTypeEnum.PERSON_LAST_NAME),
} as Person;

/**
 * Серое слово-подсказка для каждого позиционного бокса разворота: `boxId → «Фамилия» | «Имя» | …`.
 * '' — бокс пуст по замыслу формата (второй бокс однострочной подписи): подсказки в нём нет.
 * Формат роли — тот же {@link personNameFormat}, что у печати: подсказка обещает ровно напечатанное.
 * Однострочный формат — закон виньетки, как в {@link applyPersonsToVignetteModel}: вне неё боксы раздельные.
 * Чистая функция — в модель подсказка не попадает никогда: на бумаге заглушка брак, и гейт пустых
 * текстов судит её как пустое поле.
 */
export function positionalNamePlaceholders(
  spread: (Pick<SpreadData, 'collages'> & Partial<Pick<SpreadData, 'spread_type'>>) | null | undefined,
  caption: NameFormat | CaptionRules,
): Map<string, string> {
  const placeholders = new Map<string, string>();
  if (!spread) return placeholders;
  const { surfaceRules, rulesOf } = positionalCaptionRules(spread, caption);

  const collectFrom = (group: DrawableGroupData): void => {
    if (!groupCarriesPerson(group)) return;
    const rules = rulesOf(group);
    const texts = groupTexts(group);
    const teacher = isTeacherGroup(group);
    const slots = positionalSlots(texts);
    const words = positionalSlotWords(rules, teacher, slotCountOf(slots));
    slots.forEach((slot, index) => {
      const word = slot === undefined ? undefined : words[slot];
      if (word !== undefined) placeholders.set(texts[index].id, word);
    });
    // Mirrors `resolveTextByType`: a student's subject prints '' by design, so it promises no «Предмет».
    if (!teacher) for (const text of texts) if (text.textType === TextTypeEnum.PERSON_SUBJECT) placeholders.set(text.id, '');
  };

  // Группы вкладываются друг в друга — обход рекурсивный, как у остальных гейтов (`hasBlankClassmateNames`).
  const visit = (drawables: readonly DrawableData[] | undefined, parent?: DrawableGroupData): void => {
    for (const drawable of drawables ?? []) {
      // Пустое ФИО обещает то, что напечатает `resolveTextByType`: те же правила ячейки и тот же род.
      if (isDrawableText(drawable) && drawable.textType === TextTypeEnum.PERSON_FULL_NAME) {
        const teacher = !!parent && isTeacherGroup(parent);
        const rules = parent ? rulesOf(parent) : surfaceRules;
        placeholders.set(
          drawable.id,
          winningNameFormat(teacher, rules) === NameFormat.FIRST_ONLY
            ? PLACEHOLDER_PERSON.name
            : formatFullNameWithLayout(PLACEHOLDER_PERSON, printedFioLayout(teacher, rules, drawable.fioLayout)),
        );
      }

      if (!isDrawableGroup(drawable)) continue;
      collectFrom(drawable);
      visit(drawable.drawableObjects, drawable);
    }
  };

  for (const collage of spread.collages ?? []) visit(collage.drawableObjects);
  return placeholders;
}

/** Правила подписи каждой ячейки — мерка печати: вся виньетка под форматом, на обложке — карточки класса. */
function positionalCaptionRules(
  spread: Pick<SpreadData, 'collages'> & Partial<Pick<SpreadData, 'spread_type'>>,
  caption: NameFormat | CaptionRules,
): { surfaceRules: CaptionRules; rulesOf: (group: DrawableGroupData) => CaptionRules } {
  const cover = spread.spread_type === SpreadTypeEnum.COVER;
  const rosterSurface = !cover && isRosterSurface(spread);
  const surfaceRules = { ...captionRules(caption), formatWins: rosterSurface };
  const isRosterCell = rosterCellsOf(spread as unknown as RosterSurface, rosterSurface);
  const rulesOf = (group: DrawableGroupData): CaptionRules =>
    cover && isRosterCell(group as unknown as Record<string, unknown>) ? { ...surfaceRules, formatWins: true } : surfaceRules;
  return { surfaceRules, rulesOf };
}

/** Что печатает безымянная строка персонной группы: роль и, у ФИО одной строкой, его раскладка. */
export interface PositionalNameRole {
  readonly textType: TextTypeEnum;
  readonly fioLayout?: FioLayout;
}

/**
 * Роль каждого позиционного бокса разворота — та же раскладка слотов, что у {@link positionalNamePlaceholders}
 * и печати: меню редактора отмечает у безымянной строки то, что в ней напечатано. `null` — строка пуста по
 * замыслу формата (вторая у однострочной подписи) или печатает то, чего ни одна роль меню не называет
 * (инициал имени отдельной строкой).
 * Правила ячейки — как у печати: на обложке карточки класса подписываются форматом виньетки, а у посаженной
 * персоны слишком длинное ФИО одной строкой переносится тем же планировщиком подписи.
 */
export function positionalNameRoles(
  spread: (Pick<SpreadData, 'collages'> & Partial<Pick<SpreadData, 'spread_type'>>) | null | undefined,
  caption: NameFormat | CaptionRules,
  seated?: { readonly persons?: readonly Person[] | null; readonly ownerPersonId?: string | null },
): Map<string, PositionalNameRole | null> {
  const roles = new Map<string, PositionalNameRole | null>();
  if (!spread) return roles;
  const { rulesOf } = positionalCaptionRules(spread, caption);
  const personsMap = new Map((seated?.persons ?? []).map(person => [person.id, person]));
  const owner = seated?.ownerPersonId ? personsMap.get(seated.ownerPersonId) : undefined;

  const collectFrom = (group: DrawableGroupData): void => {
    if (!groupCarriesPerson(group)) return;
    const record = group as unknown as Record<string, unknown>;
    const rules = rulesOf(group);
    const person = resolveGroupPerson(record, personsMap, owner);
    const texts = groupTexts(group);
    const slots = positionalSlots(texts);
    const slotRoles = positionalSlotRoles(
      rules,
      person ? isTeacher(person) : isTeacherGroup(group),
      slotCountOf(slots),
      splitsTooWide(record, person, rules),
    );
    slots.forEach((slot, index) => {
      if (slot !== undefined && slot < slotRoles.length) roles.set(texts[index].id, slotRoles[slot]);
    });
  };

  const visit = (drawables: readonly DrawableData[] | undefined): void => {
    for (const drawable of drawables ?? []) {
      if (!isDrawableGroup(drawable)) continue;
      collectFrom(drawable);
      visit(drawable.drawableObjects);
    }
  };

  for (const collage of spread.collages ?? []) visit(collage.drawableObjects);
  return roles;
}

/** Посаженная персона, чьё ФИО одной строкой шире подписи: печать переносит его по строкам ячейки. */
function splitsTooWide(group: Record<string, unknown>, person: Person | undefined, rules: CaptionRules): boolean {
  const children = group['drawableObjects'];
  if (!person || !Array.isArray(children)) return false;
  const { boxes, photo } = captionInputsOf(children as Record<string, unknown>[]);
  const { oneLine, split } = planPersonCaptions(boxes, person, rules, photo?.width);
  return !oneLine && !!split;
}

/** Зеркало {@link positionalSlotWords} в ролях: однострочный формат кладёт ФИО целиком в первый слот. */
function positionalSlotRoles(
  rules: CaptionRules,
  teacher: boolean,
  slotCount: number,
  split = false,
): (PositionalNameRole | null)[] {
  const nameFormat = personNameFormat(teacher, rules);
  const initials = nameFormatFioLayout(nameFormat).initials === true;
  // Скрытый предмет печатается пустой строкой — отмечать в ней нечего.
  // Ученик под «только именем» печатает во второй строке '' и вне склейки.
  const blank = (textType: TextTypeEnum): boolean =>
    (initials && textType === TextTypeEnum.PERSON_FIRST_NAME) ||
    (!rules.teacherSubject && textType === TextTypeEnum.PERSON_SUBJECT) ||
    (!teacher && nameFormat === NameFormat.FIRST_ONLY && textType === TextTypeEnum.PERSON_LAST_NAME);
  const roles: (PositionalNameRole | null)[] = slotTypes(nameFormat, teacher).map(textType =>
    blank(textType) ? null : { textType },
  );
  if (split || !fillsOneBox(teacher, rules, slotCount) || !isOneLineFormat(nameFormat)) return roles;

  const [first, , ...rest] = roles;
  const merged: PositionalNameRole | null =
    nameFormat === NameFormat.FIRST_ONLY && !teacher
      ? first
      : { textType: TextTypeEnum.PERSON_FULL_NAME, fioLayout: nameFormatFioLayout(nameFormat) };
  return [merged, null, ...rest];
}

/**
 * Отчество и предмет лежат ТОЛЬКО на педагоге: у `Student` таких полей нет вовсе. Поэтому один и тот
 * же предикат отвечает и подстановке «чьё отчество брать», и меню редактора «предлагать ли отчество
 * этой персоне» — вторая копия закона развела бы меню с печатью.
 *
 * «Классный руководитель» кодируется двумя способами: domain TEACHER (+isLead) и wire-DTO 'classTeacher'.
 */
export function personHasPatronymic(person: Person): boolean {
  return person.type === PersonTypeEnum.TEACHER || (person.type as string) === FC_PersonTypeEnum.CLASS_TEACHER;
}

const isTeacher = personHasPatronymic;

const CLASS_TEACHER_ROLE = 'Классный руководитель';

/** Строка под ФИО педагога: предмет; у классрука без предмета — его роль (R212). */
function teacherRole(teacher: Teacher): string {
  const subject = (teacher.subject ?? '').trim();
  const lead = teacher.isLead === true || (teacher.type as string) === FC_PersonTypeEnum.CLASS_TEACHER;
  return subject || (lead ? CLASS_TEACHER_ROLE : '');
}

export function resolveTextByType(
  person: Person,
  textType: string | undefined,
  caption: NameFormat | CaptionRules,
  fioLayout?: FioLayout,
  options?: PersonTextOptions,
): string | undefined {
  if (!textType) return undefined;

  const rules = captionRules(caption);
  const teacher = isTeacher(person) ? (person as Teacher) : undefined;
  // strictBlanks: пустая обязательная часть отдаёт '' целиком — частичная фраза из заглушек
  // («Фамилия Пётр») такой же брак, как и полная, и гейт должен назвать поле пустым.
  const filled = (value: string | undefined | null): boolean => (value ?? '').trim() !== '';
  const strict = options?.strictBlanks === true;

  switch (textType) {
    case TextTypeEnum.PERSON_FIRST_NAME:
      return (person.name ?? '').trim();
    case TextTypeEnum.PERSON_LAST_NAME:
      return (person.surname ?? '').trim();
    case TextTypeEnum.PERSON_FULL_NAME:
      if (strict && (!filled(person.name) || !filled(person.surname))) return '';
      if (winningNameFormat(isTeacher(person), rules) === NameFormat.FIRST_ONLY) return (person.name ?? '').trim();
      return formatFullNameWithLayout(person, printedFioLayout(isTeacher(person), rules, fioLayout));
    case TextTypeEnum.PERSON_PATRONYMIC:
      return (teacher?.fatherName ?? '').trim();
    case TextTypeEnum.PERSON_GIVEN_PATRONYMIC:
      if (strict && !filled(person.name)) return '';
      return [person.name, teacher?.fatherName]
        .map(part => (part ?? '').trim())
        .filter(Boolean)
        .join(' ');
    case TextTypeEnum.PERSON_QUOTE:
      // Пустое поле печатает пустое место, не заглушку: '' проходит строгую undefined-проверку
      // applyOwnerToDrawable и затирает текст бокса; подсказка «Цитата» остаётся только в редакторе.
      // Режима «цитаты выключены» нет: рамка цитаты приходит из мастер-альбома и печатает цитату персоны.
      return person.description || '';
    case TextTypeEnum.PERSON_SUBJECT:
      return teacher && rules.teacherSubject ? teacherRole(teacher) : '';
    default:
      return undefined;
  }
}

/**
 * Раскладка, которой печатается ФИО без своей `fioLayout`: формат имён проекта — это один из пресетов.
 * Печать и отметка в подменю «Имя» читают её отсюда, поэтому галочка стоит ровно на напечатанном.
 */
export function nameFormatFioLayout(nameFormat: NameFormat): FioLayout {
  switch (nameFormat) {
    case NameFormat.FIRST_PATRONYMIC_SURNAME:
      return { order: 'givenFirst', lineBreak: 'single', includePatronymic: true };
    case NameFormat.SURNAME_BREAK_FIRST_PATRONYMIC:
      return { order: 'surnameFirst', lineBreak: 'split', includePatronymic: true };
    case NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME:
      return { order: 'givenFirst', lineBreak: 'split', includePatronymic: true };
    case NameFormat.SURNAME_INITIALS:
      return { order: 'surnameFirst', lineBreak: 'single', includePatronymic: true, initials: true };
    case NameFormat.INITIALS_SURNAME:
      return { order: 'givenFirst', lineBreak: 'single', includePatronymic: true, initials: true };
    case NameFormat.SURNAME_BREAK_FIRST_BREAK_PATRONYMIC:
      return { order: 'surnameFirst', lineBreak: 'perPart', includePatronymic: true };
    case NameFormat.FIRST_BREAK_PATRONYMIC_BREAK_SURNAME:
      return { order: 'givenFirst', lineBreak: 'perPart', includePatronymic: true };
    // Формат вне перечисления (настройки проекта из старой схемы) печатается дефолтом генератора, а не роняет подстановку.
    case NameFormat.SURNAME_FIRST_PATRONYMIC:
    default:
      return { order: 'surnameFirst', lineBreak: 'single', includePatronymic: true };
  }
}

/**
 * Раскладка, которой `resolveTextByType` печатает ФИО: формат панели, где он побеждает, переписывает порядок,
 * инициалы и перенос рамки (отчество остаётся за рамкой); иначе своя раскладка рамки, без неё — пресет формата.
 * «Только имя» раскладкой не выражается — его называет `winningNameFormat`.
 */
export function printedFioLayout(teacher: boolean, caption: NameFormat | CaptionRules, fioLayout?: FioLayout): FioLayout {
  const rules = captionRules(caption);
  const winning = winningNameFormat(teacher, rules);
  // `keepLine` держит строку: перенос — ни формата, ни своей раскладки — бокс одной строкой не ломает.
  if (!winning) {
    if (fioLayout) return fioLayout.keepLine ? { ...fioLayout, lineBreak: 'single' } : fioLayout;
    return nameFormatFioLayout(captionFormat(teacher, rules) ?? NameFormat.SURNAME_FIRST_PATRONYMIC);
  }

  const preset = nameFormatFioLayout(winning);
  const { order, initials } = preset;
  const lineBreak = fioLayout?.keepLine ? 'single' : preset.lineBreak;
  return { ...preset, ...fioLayout, order, initials, lineBreak };
}

/**
 * Выбор формата в панели виньетки — последнее слово о порядке слов: каждое ФИО виньетки со своей
 * раскладкой берёт порядок и инициалы формата, перенос строки и отчество остаются свои.
 */
export function applyNameFormatToVignetteLayouts<T extends PhotoBookModel>(model: T, nameFormat: NameFormat): T {
  const modelRecord = model as Record<string, unknown> | null | undefined;
  if (!modelRecord || !Array.isArray(modelRecord['spreads'])) return model;

  const { order, initials } = nameFormatFioLayout(nameFormat);
  const relayout = (drawables: Record<string, unknown>[]): Record<string, unknown>[] =>
    drawables.map(obj => {
      if (obj['type'] === 'group' && Array.isArray(obj['drawableObjects'])) {
        return { ...obj, drawableObjects: relayout(obj['drawableObjects']) };
      }

      const fioLayout = obj['fioLayout'] as FioLayout | undefined;
      if (obj['type'] !== 'text' || obj['textType'] !== TextTypeEnum.PERSON_FULL_NAME || !fioLayout) return obj;
      return { ...obj, fioLayout: { ...fioLayout, order, initials } };
    });

  return {
    ...modelRecord,
    spreads: modelRecord['spreads'].map((spread: Record<string, unknown>) =>
      spread['spread_type'] !== SpreadTypeEnum.VIGNETTE || !Array.isArray(spread['collages'])
        ? spread
        : {
            ...spread,
            collages: spread['collages'].map((collage: Record<string, unknown>) =>
              Array.isArray(collage['drawableObjects'])
                ? { ...collage, drawableObjects: relayout(collage['drawableObjects']) }
                : collage,
            ),
          },
    ),
  } as T;
}

export function formatFullName(person: Person, nameFormat: NameFormat): string {
  return formatFullNameWithLayout(person, nameFormatFioLayout(nameFormat));
}

export function formatFullNameWithLayout(person: Person, fioLayout: FioLayout): string {
  // Пустое поле даёт пустую часть ФИО: строка из одних пробелов тримится до '' и выпадает из групп.
  const surname = (person.surname ?? '').trim();
  const rawName = (person.name ?? '').trim();
  const rawPatronymic = fioLayout.includePatronymic && isTeacher(person) ? ((person as Teacher).fatherName || '').trim() : '';
  const initial = (part: string): string => (part.charAt(0) ? `${part.charAt(0)}.` : '');
  const name = fioLayout.initials ? initial(rawName) : rawName;
  const patronymic = fioLayout.initials ? initial(rawPatronymic) : rawPatronymic;
  const given =
    fioLayout.lineBreak === 'perPart'
      ? [name, patronymic]
      : [[name, patronymic].filter(Boolean).join(fioLayout.initials ? '' : ' ')];
  const ordered = fioLayout.order === 'givenFirst' ? [...given, surname] : [surname, ...given];
  // Строка — поле карточки целиком: двойная фамилия или составное имя свою строку не делят.
  const lines = ordered.map(g => g.replace(/\s+/g, ' ').trim()).filter(Boolean);
  return lines.join(fioLayout.lineBreak === 'single' ? ' ' : '\n');
}

/** Действующий формат роли — один для печати позиционных слотов и для их серых подсказок. */
function personNameFormat(teacher: boolean, rules: CaptionRules): NameFormat {
  return captionFormat(teacher, rules) ?? NameFormat.SURNAME_FIRST_PATRONYMIC;
}

/** Полное ФИО одной строкой, шире подписи, переносится; инициалы и одно имя остаются строкой и ужимаются. */
export function wrapsWhenTooWide(nameFormat: NameFormat | null): boolean {
  return (
    nameFormat === null ||
    nameFormat === NameFormat.SURNAME_FIRST_PATRONYMIC ||
    nameFormat === NameFormat.FIRST_PATRONYMIC_SURNAME
  );
}

function isOneLineFormat(nameFormat: NameFormat): boolean {
  return nameFormatFioLayout(nameFormat).lineBreak === 'single';
}

// Подписи — закон виньетки: обложка и обычный разворот (`formatWins: false`) держат позиционные боксы как были.
// «Как в паке» для всех — тоже: строки ячейки печатают то, что разложил по ним дизайнер пака; а единственная
// безымянная строка несёт ФИО целиком, как прежний «Как в паке» у педагогов, — иначе имя не попало бы на бумагу.
function fillsOneBox(teacher: boolean, rules: CaptionRules, slotCount: number): boolean {
  if (rules.formatWins === false) return false;
  if (rules.fromPack) return slotCount === 1;
  return isOneLineFormat(personNameFormat(teacher, rules));
}

function slotCountOf(slots: readonly (number | undefined)[]): number {
  return slots.filter(slot => slot !== undefined).length;
}

/** Однострочный формат кладёт ФИО целиком в первый бокс и гасит второй: иначе ячейка дизайна печатает его в две строки. */
function fitSlotsToFormat(slots: string[], nameFormat: NameFormat, teacher: boolean, strict = false): string[] {
  if (!isOneLineFormat(nameFormat)) return slots;

  const [first, second, ...rest] = slots;
  const parts = nameFormat === NameFormat.FIRST_ONLY && !teacher ? [first] : [first, second];
  // strictBlanks: пустая часть гасит строку целиком, иначе гейт примет «Акулик» без имени за заполненное.
  return [strict && parts.some(part => !part) ? '' : parts.filter(Boolean).join(' '), '', ...rest];
}

function getPersonTexts(person: Person, rules: CaptionRules, slotCount: number, strict = false): string[] {
  const teacher = isTeacher(person);
  const nameFormat = personNameFormat(teacher, rules);
  const slots = personNameSlots(person, rules, nameFormat, strict);
  return fillsOneBox(teacher, rules, slotCount) ? fitSlotsToFormat(slots, nameFormat, teacher, strict) : slots;
}

function personNameSlots(person: Person, rules: CaptionRules, nameFormat: NameFormat, strict: boolean): string[] {
  const surname = (person.surname ?? '').trim();
  const name = (person.name ?? '').trim();
  const nameInitial = name.charAt(0) ? `${name.charAt(0)}.` : '';

  if (isTeacher(person)) {
    const teacher = person as Teacher;
    // Как PERSON_GIVEN_PATRONYMIC в resolveTextByType: отчество без имени под strictBlanks — пустота.
    const fatherName = strict && !name ? '' : (teacher.fatherName ?? '').trim();
    const subject = rules.teacherSubject ? teacherRole(teacher) : '';
    const namePatronymic = [name, fatherName].filter(Boolean).join(' ');
    const patronymicInitial = fatherName && fatherName.charAt(0) ? `${fatherName.charAt(0)}.` : '';
    switch (nameFormat) {
      case NameFormat.FIRST_PATRONYMIC_SURNAME:
      case NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME:
      case NameFormat.FIRST_BREAK_PATRONYMIC_BREAK_SURNAME:
        return [namePatronymic, surname, subject];
      case NameFormat.INITIALS_SURNAME:
        return [`${nameInitial}${patronymicInitial}`, surname, subject];
      case NameFormat.SURNAME_INITIALS:
        return [surname, `${nameInitial}${patronymicInitial}`, subject];
      default:
        return [surname, namePatronymic, subject];
    }
  }

  switch (nameFormat) {
    case NameFormat.FIRST_PATRONYMIC_SURNAME:
    case NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME:
      return [name, surname];
    case NameFormat.FIRST_ONLY:
      return [name, ''];
    case NameFormat.SURNAME_INITIALS:
      return [surname, nameInitial];
    default:
      return [surname, name];
  }
}
